import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import ContractFeeSettings from './ContractFeeSettings';

const ProjectContracts = () => {
  // Currency formatting function
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0.00';
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [projectCostCodes, setProjectCostCodes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [projectBudgetLines, setProjectBudgetLines] = useState([]);
  const [unallocatedCosts, setUnallocatedCosts] = useState([]);
  const [externalChangeOrders, setExternalChangeOrders] = useState([]);
  const [changeOrderLines, setChangeOrderLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copyingBudgetLines, setCopyingBudgetLines] = useState(false);
  const [budgetLinesProgress, setBudgetLinesProgress] = useState({ current: 0, total: 0 });
  
  // Allocation management state
  const [showAllocationsModal, setShowAllocationsModal] = useState(false);
  const [selectedItemForAllocations, setSelectedItemForAllocations] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationsByItem, setAllocationsByItem] = useState({});
  const [newAllocation, setNewAllocation] = useState({
    cost_code_vuid: '',
    cost_type_vuid: '',
  });
  
  // Add allocation form states
  const [showAddAllocationForm, setShowAddAllocationForm] = useState(false);
  const [itemAllocations, setItemAllocations] = useState([]);
  
  // Fee level and fee settings state
  const [feeLevel, setFeeLevel] = useState(null);
  const [feeSettings, setFeeSettings] = useState([]);
  
  // Integration states
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [selectedContractForIntegration, setSelectedContractForIntegration] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [sendingToIntegration, setSendingToIntegration] = useState(false);
  
  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showItemsForm, setShowItemsForm] = useState(false);
  const [showFeeSettings, setShowFeeSettings] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  
  // Ref to track last processed contract to prevent infinite loops
  const lastProcessedContractRef = useRef(null);
  // Ref for the contract form header to scroll/focus when creating new contract
  const contractFormHeaderRef = useRef(null);
  
  // Form data
  const CONTRACT_TYPES = [
    { value: 'cost_plus', label: 'Cost Plus' },
    { value: 'gmp', label: 'GMP' },
    { value: 'time_and_materials', label: 'T&M' },
    { value: 'fixed_price', label: 'Fixed Price' },
  ];

  const [formData, setFormData] = useState({
    project_vuid: '',
    contract_number: '',
    contract_date: '',
    customer_vuid: '',
    accounting_period_vuid: '',
    contract_type: CONTRACT_TYPES[0].value,
    status: 'active',
    description: '',
    total_amount: '',
    currency: 'USD',
    payment_terms: '',
    delivery_terms: '',
    warranty_terms: '',
    start_date: '',
    end_date: '',
    delivery_date: '',
    notes: ''
  });
  
  const [itemFormData, setItemFormData] = useState({
    item_number: '',
    description: '',
    quantity: 1,
    unit_of_measure: 'EA',
    unit_price: '',
    total_amount: '',
    markup_percentage: 0,
    status: 'active',
    cost_code_vuid: null,
    cost_type_vuid: null,
    specifications: '',
    delivery_location: '',
    delivery_date: '',
    warranty_info: '',
    notes: '',
    contract_vuid: ''
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [contractsPerPage] = useState(10);
  
  // Project settings and allocation status
  const [projectSettings, setProjectSettings] = useState({});
  const [allocationStatus, setAllocationStatus] = useState({});
  const [showAllocationAlert, setShowAllocationAlert] = useState(false);
  
  // Dropdown states
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [accountingPeriodDropdownOpen, setAccountingPeriodDropdownOpen] = useState(false);
  const [contractTypeDropdownOpen, setContractTypeDropdownOpen] = useState(false);
  
  // Search states for smart dropdowns
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  // Errors
  const [errors, setErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  // State for contract items grid
  const [newItemData, setNewItemData] = useState({
    item_number: '',
    cost_code_vuid: '',
    cost_type_vuid: '',
    description: '',
    total_amount: '',
    markup_percentage: 0,
    notes: ''
  });

  // State for dropdowns
  const [costCodeDropdownOpen, setCostCodeDropdownOpen] = useState(null);
  const [costTypeDropdownOpen, setCostTypeDropdownOpen] = useState(null);
  const [newItemCostCodeDropdownOpen, setNewItemCostCodeDropdownOpen] = useState(false);
  const [newItemCostTypeDropdownOpen, setNewItemCostTypeDropdownOpen] = useState(false);

  // State for filtered dropdowns
  const [filteredCostCodes, setFilteredCostCodes] = useState([]);
  const [filteredCostTypes, setFilteredCostTypes] = useState([]);

  const baseURL = 'http://localhost:5001';

  useEffect(() => {
    fetchData();
  }, []);

  // Add click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setProjectDropdownOpen(false);
        setStatusDropdownOpen(false);
        setCustomerDropdownOpen(false);
        setAccountingPeriodDropdownOpen(false);
        setContractTypeDropdownOpen(false);
        setCostCodeDropdownOpen(null);
        setCostTypeDropdownOpen(null);
        setNewItemCostCodeDropdownOpen(false);
        setNewItemCostTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    filterAndPaginateContracts();
  }, [contracts, searchTerm, projectFilter, statusFilter, currentPage]);

  // Reset cost type selection when cost code changes
  useEffect(() => {
    if (newAllocation.cost_code_vuid) {
      setNewAllocation(prev => ({ ...prev, cost_type_vuid: '' }));
    }
  }, [newAllocation.cost_code_vuid]);

  // Fetch project's budget line combinations when a contract is selected/viewed
  useEffect(() => {
    const projectVuid = selectedContract?.project_vuid;
    if (projectVuid) {
      axios
        .get(`${baseURL}/api/projects/${projectVuid}/budget-lines`)
        .then((res) => setProjectBudgetLines(res.data || []))
        .catch((err) => {
          console.warn('Failed to fetch project budget lines:', err);
          setProjectBudgetLines([]);
        });
    } else {
      setProjectBudgetLines([]);
    }
  }, [selectedContract]);

  // Handle query parameters for project filtering and contract viewing
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectVuid = searchParams.get('project');
    const contractVuid = searchParams.get('contract');
    
    if (projectVuid) {
      // Pre-filter contracts for this project
      setProjectFilter(projectVuid);
      
      // If there's also a create parameter, open the create form
      if (searchParams.get('create') === 'true') {
        setFormData(prev => ({
          ...prev,
          project_vuid: projectVuid
        }));
        setShowCreateForm(true);
        setEditingContract(null); // Ensure we're in create mode, not edit mode
        
        // Remove the 'create' parameter from URL to prevent reopening on refresh
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('create');
        const newSearch = newSearchParams.toString();
        navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true });
        
        // Scroll to and focus on the contract form header when creating from URL
        setTimeout(() => {
          if (contractFormHeaderRef.current) {
            contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Focus on the first input field (project selector)
            setTimeout(() => {
              const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
              if (firstInput) {
                firstInput.focus();
              }
            }, 100);
          }
        }, 200);
      }
      
      // Refetch data with project filter
      fetchData();
      
      // Fetch project settings for allocation alerts
      fetchProjectSettings(projectVuid);
    }
  }, [location.search]);

  // Handle contract selection when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const contractVuid = searchParams.get('contract');
    
    if (contractVuid) {
      // Reset ref when URL changes to allow processing new contract
      if (lastProcessedContractRef.current !== contractVuid) {
        lastProcessedContractRef.current = null;
      }
    } else {
      // No contract in URL, reset the ref
      lastProcessedContractRef.current = null;
    }
  }, [location.search]);

  // Separate effect to handle contract selection after contracts are loaded
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const contractVuid = searchParams.get('contract');
    
    if (!contractVuid) return;
    
    // Skip if we've already processed this contract
    if (lastProcessedContractRef.current === contractVuid) {
      return;
    }
    
    // Find and select the contract to view
    const contract = contracts.find(c => c.vuid === contractVuid);
    
    if (contract) {
      // Only load if not already selected to avoid re-triggering
      if (!selectedContract || selectedContract.vuid !== contractVuid) {
        lastProcessedContractRef.current = contractVuid;
        setSelectedContract(contract);
        loadContractData(contract);
      }
    } else if (contracts.length > 0) {
      // Contract not found in local state, try to fetch it directly (only once)
      lastProcessedContractRef.current = contractVuid; // Mark as processing to prevent duplicate requests
      axios.get(`${baseURL}/api/project-contracts/${contractVuid}`)
        .then(response => {
          const fetchedContract = response.data;
          if (fetchedContract) {
            // Add to contracts list if not already there
            setContracts(prev => {
              const exists = prev.find(c => c.vuid === fetchedContract.vuid);
              if (!exists) {
                return [...prev, fetchedContract];
              }
              return prev;
            });
            setSelectedContract(fetchedContract);
            loadContractData(fetchedContract);
          }
        })
        .catch(err => {
          console.error('Contract not found:', err);
          lastProcessedContractRef.current = null; // Reset on error so we can try again if URL changes
          setError(`Contract not found. The contract may have been deleted.`);
          // Clear the contract parameter from URL
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete('contract');
          navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
        });
    }
  }, [location.search, contracts.length]); // Only depend on contracts.length to avoid full array dependency

  // Helper function to load contract-related data
  const loadContractData = (contract) => {
    if (!contract) return;
    
    // Fetch project-specific cost codes for this contract's project
    axios.get(`${baseURL}/api/projects/${contract.project_vuid}/cost-codes`)
      .then(response => {
        setProjectCostCodes(response.data || []);
        console.log(`Fetched ${response.data?.length || 0} project cost codes for contract ${contract.contract_number}`);
      })
      .catch(e => {
        console.warn('Failed to fetch project cost codes:', e);
        setProjectCostCodes([]);
      });
    
    // Fetch project budget lines for this contract's project
    axios.get(`${baseURL}/api/projects/${contract.project_vuid}/budget-lines`)
      .then(response => {
        setProjectBudgetLines(response.data || []);
        console.log(`Fetched ${response.data?.length || 0} budget lines for contract ${contract.contract_number}`);
      })
      .catch(e => {
        console.warn('Failed to fetch project budget lines:', e);
        setProjectBudgetLines([]);
      });
    
    fetchContractItems(contract.vuid);
  };

  // Fetch project settings when project filter changes
  useEffect(() => {
    if (projectFilter && projectFilter !== 'all') {
      fetchProjectSettings(projectFilter);
    } else {
      setShowAllocationAlert(false);
      setAllocationStatus({});
    }
  }, [projectFilter]);

  // Log project cost codes when they change
  useEffect(() => {
    if (projectCostCodes.length > 0) {
      console.log('Project cost codes loaded:', projectCostCodes.length);
    }
  }, [projectCostCodes]);

  // Fetch budget lines when project is selected in create form
  useEffect(() => {
    if (formData.project_vuid && showCreateForm) {
      axios
        .get(`${baseURL}/api/projects/${formData.project_vuid}/budget-lines`)
        .then((res) => {
          setProjectBudgetLines(res.data || []);
          console.log(`Fetched ${res.data?.length || 0} budget lines for project ${formData.project_vuid}`);
        })
        .catch((err) => {
          console.warn('Failed to fetch project budget lines for create form:', err);
          setProjectBudgetLines([]);
        });
    } else if (!formData.project_vuid) {
      setProjectBudgetLines([]);
    }
  }, [formData.project_vuid, showCreateForm]);

  // Focus on contract form header when creating a new contract (not editing)
  useEffect(() => {
    if (showCreateForm && !editingContract) {
      // Small delay to ensure the form is rendered
      setTimeout(() => {
        if (contractFormHeaderRef.current) {
          contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Focus on the first input field (project selector)
          setTimeout(() => {
            const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
            if (firstInput) {
              firstInput.focus();
            }
          }, 100);
        }
      }, 100);
    }
  }, [showCreateForm, editingContract]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setProjectDropdownOpen(false);
        setCustomerDropdownOpen(false);
        setAccountingPeriodDropdownOpen(false);
        setContractTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('Fetching data from APIs...');
      
      // Check if we have a project filter from URL params
      const searchParams = new URLSearchParams(location.search);
      const projectVuid = searchParams.get('project');
      
      // Build the contracts URL with project filter if available
      let contractsUrl = `${baseURL}/api/project-contracts`;
      if (projectVuid) {
        contractsUrl += `?project_vuid=${projectVuid}`;
      }
      
      // Fetch core data first (customers endpoint is optional)
      const [
        contractsRes,
        projectsRes,
        customersRes,
        accountingPeriodsRes
      ] = await Promise.all([
        axios.get(contractsUrl),
        axios.get(`${baseURL}/api/projects`),
        axios.get(`${baseURL}/api/customers`).catch((error) => {
          if (error.response && error.response.status === 404) {
            console.warn('Customers endpoint not available, using empty list.');
            return { data: [] };
          }
          throw error;
        }),
        axios.get(`${baseURL}/api/accounting-periods/open`)
      ]);
      
      console.log('Core data fetched successfully');
      const projectList = projectsRes.data || [];
      setContracts(contractsRes.data || []);
      setProjects(projectList);
      
      let customerOptions = customersRes.data || [];
      if (!customerOptions || customerOptions.length === 0) {
        const uniqueCustomers = new Map();
        projectList.forEach((project) => {
          const baseName = project.client_name || project.project_name || 'Unknown Customer';
          const displayName = project.client_name ? baseName : `${baseName} (Project)`;
          const number = project.project_number || '';
          const key = project.client_name
            ? `${project.client_name.toLowerCase()}`
            : `project-${project.vuid}`;

          if (!uniqueCustomers.has(key)) {
            uniqueCustomers.set(key, {
              vuid: key,
              customer_name: displayName,
              customer_number: number
            });
          }
        });
        customerOptions = Array.from(uniqueCustomers.values());
      }
      setCustomers(customerOptions);
      const periodList = accountingPeriodsRes.data || [];
      setAccountingPeriods(periodList);

      if (!formData.accounting_period_vuid && periodList.length === 1) {
        const singlePeriod = periodList[0];
        setFormData((prev) => ({
          ...prev,
          accounting_period_vuid: singlePeriod.vuid
        }));
      }
      // Try to fetch cost codes and cost types, but don't fail if they don't work
      try {
        const costCodesRes = await axios.get(`${baseURL}/api/costcodes`);
        setCostCodes(costCodesRes.data || []);
        console.log('Cost codes fetched successfully');
      } catch (costCodesErr) {
        console.warn('Failed to fetch cost codes:', costCodesErr);
        setCostCodes([]);
      }
      
      try {
        const costTypesRes = await axios.get(`${baseURL}/api/costtypes`);
        setCostTypes(costTypesRes.data || []);
        console.log('Cost types fetched successfully');
      } catch (costTypesErr) {
        console.warn('Failed to fetch cost types:', costTypesErr);
        setCostTypes([]);
      }
      
      // Try to fetch integrations
      try {
        const integrationsRes = await axios.get(`${baseURL}/api/integrations`);
        
        // Filter integrations to only show those that have 'contracts' enabled
        const filteredIntegrations = integrationsRes.data.filter(integration => 
          integration.enabled_objects && integration.enabled_objects.contracts === true
        );
        
        setIntegrations(filteredIntegrations || []);
        console.log('Integrations fetched successfully');
      } catch (integrationsErr) {
        console.warn('Failed to fetch integrations:', integrationsErr);
        setIntegrations([]);
      }
      
      setError(null);
      console.log('All data fetched successfully');
      
      // Safely refresh contract amounts after data is loaded
      if (contractsRes.data && contractsRes.data.length > 0) {
        setTimeout(() => {
          refreshContractAmounts().catch(err => {
            console.warn('Background contract amount refresh failed:', err);
          });
        }, 1000); // Delay by + 1 second to avoid blocking the UI
      }
    } catch (err) {
      console.error('Error fetching core data:', err);
      setError(`Failed to load data: ${err.message}`);
      // Set empty arrays as fallback
      setContracts([]);
      setProjects([]);
      setCustomers([]);
      setAccountingPeriods([]);
      setCostCodes([]);
      setCostTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectSettings = async (projectVuid) => {
    if (!projectVuid) return;
    
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}/settings`);
      setProjectSettings(response.data || {});
      
      // Check if we should show allocation alerts
      const shouldShowAlerts = response.data?.allocate_contract_lines_to_cost_codes || false;
      setShowAllocationAlert(shouldShowAlerts);
      
      if (shouldShowAlerts) {
        // Fetch allocation status if alerts are enabled
        await fetchAllocationStatus(projectVuid);
      }
    } catch (error) {
      console.warn('Failed to fetch project settings:', error);
      setProjectSettings({});
      setShowAllocationAlert(false);
    }
  };

  const fetchAllocationStatus = async (projectVuid) => {
    if (!projectVuid) return;
    
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}/budget-allocation-status`);
      setAllocationStatus(response.data || {});
    } catch (error) {
      console.warn('Failed to fetch allocation status:', error);
      setAllocationStatus({});
    }
  };



  const filterAndPaginateContracts = () => {
    try {
      let filtered = contracts.filter(contract => {
        const matchesSearch = !searchTerm || 
          (contract.contract_number && contract.contract_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (contract.description && contract.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (contract.customer && contract.customer.customer_name && contract.customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesProject = projectFilter === 'all' || contract.project_vuid === projectFilter;
        const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
        
        return matchesSearch && matchesProject && matchesStatus;
      });
      
      setFilteredContracts(filtered);
    } catch (err) {
      console.error('Error filtering contracts:', err);
      setFilteredContracts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field immediately when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleItemInputChange = (e) => {
    const { name, value } = e.target;
    setItemFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-calculate total amount
    if (name === 'quantity' || name === 'unit_price') {
      const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(itemFormData.quantity) || 0;
      const unitPrice = name === 'unit_price' ? parseFloat(value) || 0 : parseFloat(itemFormData.unit_price) || 0;
      setItemFormData(prev => ({
        ...prev,
        total_amount: (quantity * unitPrice).toFixed(2)
      }));
    }
    
    // Clear error for this field
    if (itemErrors[name]) {
      setItemErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    console.log('Starting form validation...');
    console.log('Current form data:', formData);
    
    const newErrors = {};
    if (!formData.project_vuid) {
      newErrors.project_vuid = 'Project is required';
      console.log('Project validation failed - project_vuid is empty');
    }
    if (!formData.contract_number) {
      newErrors.contract_number = 'Contract number is required';
      console.log('Contract number validation failed - contract_number is empty');
    }
    if (!formData.description) {
      newErrors.description = 'Description is required';
      console.log('Description validation failed - description is empty');
    }
    if (!formData.contract_type) {
      newErrors.contract_type = 'Contract type is required';
      console.log('Contract type validation failed - contract_type is empty');
    }
          if (!formData.customer_vuid) {
        newErrors.customer_vuid = 'Customer is required';
        console.log('Customer validation failed - customer_vuid is empty');
      }
      if (!formData.accounting_period_vuid) {
        newErrors.accounting_period_vuid = 'Accounting period is required';
        console.log('Accounting period validation failed - accounting_period_vuid is empty');
      }
    // Start and end dates are optional - removed required validation
    // if (!formData.start_date) {
    //   newErrors.start_date = 'Start date is required';
    //   console.log('Start date validation failed - start_date is empty');
    // }
    // if (!formData.end_date) {
    //   newErrors.end_date = 'End date is required';
    //   console.log('End date validation failed - end_date is empty');
    // }
    
    console.log('Validation errors found:', newErrors);
    setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
    console.log('Form validation result:', isValid);
    return isValid;
  };

  const validateItemForm = () => {
    const newErrors = {};
    if (!itemFormData.item_number) newErrors.item_number = 'Item number is required';
    if (!itemFormData.description) newErrors.description = 'Description is required';
    if (!itemFormData.quantity) newErrors.quantity = 'Quantity is required';
    if (!itemFormData.unit_price) newErrors.unit_price = 'Unit price is required';
    
    setItemErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submitted!');
    console.log('Form data:', formData);
    
    if (!validateForm()) {
      console.log('Form validation failed');
      return;
    }
    
    console.log('Form validation passed, proceeding with submission');
    
    try {
      let response;
      // Compute amount from current items if available, otherwise 0 for new contracts
      const computedAmount = Array.isArray(contractItems) && contractItems.length > 0
        ? contractItems.reduce((sum, it) => sum + (parseFloat(it.total_amount || 0) || 0), 0)
        : 0;

      // Map frontend fields to backend expectations
      const payload = {
        project_vuid: formData.project_vuid,
        contract_number: formData.contract_number,
        contract_name: formData.description,
        contract_type: formData.contract_type,
        contract_amount: computedAmount,
        customer_vuid: formData.customer_vuid || null,
        accounting_period_vuid: formData.accounting_period_vuid,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        notes: formData.notes || null,
      };

      console.log('Sending payload:', payload);

      if (editingContract) {
        console.log('Updating existing contract');
        response = await axios.put(`${baseURL}/api/project-contracts/${editingContract.vuid}`, payload);
        await fetchData();
        resetForm();
        setSuccessMessage('Contract updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Creating new contract
        console.log('Creating new contract');
        response = await axios.post(`${baseURL}/api/project-contracts`, payload);
        console.log('Response received:', response);
        await fetchData();
        
        // Get the newly created contract to show its items form
        const newContract = response.data;
        if (newContract && newContract.vuid) {
          // Set the newly created contract as selected and show items form
          setSelectedContract(newContract);
          setShowItemsForm(true);
          setShowCreateForm(false);
          
          // Pre-populate the item form with the new contract's VUID
          setItemFormData(prev => ({
            ...prev,
            contract_vuid: newContract.vuid,
            markup_percentage: budgetIncreasePercentage
          }));
          
          // Optionally generate items from budget lines
          if (createItemsFromBudget) {
            setCopyingBudgetLines(true);
            setBudgetLinesProgress({ current: 0, total: 0 });
            try {
              await generateItemsFromBudget(newContract);
              // Items are already added to state in generateItemsFromBudget
              // No need to refetch - just ensure contract is selected
            } finally {
              setCopyingBudgetLines(false);
              setBudgetLinesProgress({ current: 0, total: 0 });
            }
          } else {
            // Only fetch items if we didn't generate them from budget
            await fetchContractItems(newContract.vuid);
          }
          
          // Ensure the contract is still selected
          setSelectedContract(newContract);
          
          // Show success message
          setSuccessMessage(`Contract "${newContract.contract_number}" created successfully! Now add your contract items below.`);
          setError(''); // Clear any previous errors
          return; // Don't call resetForm yet, let user add contract items
        }
      }
      
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error saving contract:', err);
      if (err.response && err.response.data && err.response.data.error) {
        setError('Error saving contract: ' + err.response.data.error);
      } else {
        setError('Error saving contract: ' + err.message);
      }
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateItemForm()) return;
    
    // Debug logging
    console.log('handleItemSubmit - editingItem:', editingItem);
    console.log('handleItemSubmit - selectedContract:', selectedContract);
    console.log('handleItemSubmit - itemFormData:', itemFormData);
    
    // Check if we have a selected contract for new items
    if (!editingItem && !selectedContract) {
      console.log('No contract selected - showing error');
      setError('No contract selected. Please view contract items first.');
      return;
    }
    
    try {
      let savedItem;
      const sanitizedFormData = {
        ...itemFormData,
        markup_percentage: parseFloat(itemFormData.markup_percentage) || 0
      };
      if (editingItem) {
        console.log('Updating existing item');
        const response = await axios.put(`${baseURL}/api/project-contract-items/${editingItem.vuid}`, sanitizedFormData);
        savedItem = response.data;
        setContractItems(prev => prev.map(item => item.vuid === editingItem.vuid ? savedItem : item));
        
        // Refresh contract data to update total amount
        const contractResponse = await axios.get(`${baseURL}/api/project-contracts/${editingItem.contract_vuid}`);
        const updatedContract = contractResponse.data;
        setContracts(prev => prev.map(c => c.vuid === editingItem.contract_vuid ? updatedContract : c));
        if (selectedContract && selectedContract.vuid === editingItem.contract_vuid) {
          setSelectedContract(updatedContract);
        }
      } else {
        console.log('Creating new item with contract_vuid:', selectedContract.vuid);
        
        // Clean the data - convert empty strings to null for optional fields
        const cleanedData = {
          ...sanitizedFormData,
          contract_vuid: selectedContract.vuid,
          cost_code_vuid: itemFormData.cost_code_vuid || null,
          cost_type_vuid: itemFormData.cost_type_vuid || null,
          specifications: itemFormData.specifications || null,
          delivery_location: itemFormData.delivery_location || null,
          delivery_date: itemFormData.delivery_date || null,
          warranty_info: itemFormData.warranty_info || null,
          notes: itemFormData.notes || null
        };
        
        const requestData = cleanedData;
        console.log('Request data being sent:', requestData);
        console.log('API endpoint:', `${baseURL}/api/project-contract-items`);
        const response = await axios.post(`${baseURL}/api/project-contract-items`, requestData);
        savedItem = response.data;
        
        setContractItems(prev => [...prev, savedItem]);
        
        // Refresh contract data to update total amount
        const contractResponse = await axios.get(`${baseURL}/api/project-contracts/${selectedContract.vuid}`);
        const updatedContract = contractResponse.data;
        setContracts(prev => prev.map(c => c.vuid === selectedContract.vuid ? updatedContract : c));
        setSelectedContract(updatedContract);
      }
      
      // Only show success message if API call succeeded
      setSuccessMessage('Item saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      resetItemForm();
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving item:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      if (errorMessage.includes('accounting period is closed')) {
        setError(`Cannot save item: The accounting period for this contract is closed. Please create a change order to add new items.`);
      } else {
        setError(`Error saving item: ${errorMessage}`);
      }
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemFormData({
      item_number: item.item_number || '',
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_of_measure: item.unit_of_measure || 'EA',
      unit_price: item.unit_price || '',
      total_amount: item.total_amount || '',
      markup_percentage: item.markup_percentage ?? budgetIncreasePercentage,
      status: item.status || 'active',
      cost_code_vuid: item.cost_code_vuid || null,
      cost_type_vuid: item.cost_type_vuid || null,
      specifications: item.specifications || '',
      delivery_location: item.delivery_location || '',
      delivery_date: item.delivery_date || '',
      warranty_info: item.warranty_info || '',
      notes: item.notes || '',
      contract_vuid: item.contract_vuid || (selectedContract ? selectedContract.vuid : '')
    });
    setShowItemsForm(true);
  };

  const handleDeleteItem = async (itemVuid) => {
    if (itemVuid.startsWith('temp-')) {
      // Remove temporary item from local state
      setContractItems(prev => prev.filter(item => item.vuid !== itemVuid));
    } else {
      // Delete existing item from backend
      try {
        await axios.delete(`${baseURL}/api/project-contract-items/${itemVuid}`);
        await fetchContractItems(selectedContract.vuid);
        setSuccessMessage('Contract item deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Error deleting contract item: ' + err.message);
      }
    }
  };

  // Handle manage allocations
  const handleManageAllocations = async (item) => {
    console.log('🔧 handleManageAllocations called with item:', item);
    setSelectedItemForAllocations(item);
    
    // Find the contract that contains this item
    const contract = contracts.find(c => c.vuid === item.contract_vuid);
    if (contract) {
      setSelectedContract(contract);
    }
    
    // Get the project VUID from the contract
    const projectVuid = contract?.project_vuid || selectedContract?.project_vuid;
    
    // Fetch unallocated costs for this project to populate dropdown options
    if (projectVuid) {
      try {
        // Get open accounting period for this project
        const openPeriodsRes = await axios.get(`${baseURL}/api/accounting-periods/open`);
        const openPeriods = openPeriodsRes.data || [];
        const openPeriod = openPeriods.length === 1 ? openPeriods[0] : null;
        
        if (openPeriod) {
          const unallocatedRes = await axios.get(
            `${baseURL}/api/project-billings/unallocated-costs?project_vuid=${projectVuid}&accounting_period_vuid=${openPeriod.vuid}`
          );
          if (unallocatedRes.data?.success && unallocatedRes.data?.unallocated_costs) {
            setUnallocatedCosts(unallocatedRes.data.unallocated_costs || []);
            console.log('Fetched unallocated costs:', unallocatedRes.data.unallocated_costs.length);
          }
        } else {
          // Fetch without period filter if no open period
          const unallocatedRes = await axios.get(
            `${baseURL}/api/project-billings/unallocated-costs?project_vuid=${projectVuid}`
          );
          if (unallocatedRes.data?.success && unallocatedRes.data?.unallocated_costs) {
            setUnallocatedCosts(unallocatedRes.data.unallocated_costs || []);
          }
        }
      } catch (err) {
        console.warn('Failed to fetch unallocated costs:', err);
        setUnallocatedCosts([]);
      }
    }
    
    setShowAllocationsModal(true);
    console.log('🔧 Modal should be showing now');
    
    // Load existing allocations for this item if it has a vuid (not a new item)
    if (item.vuid && !item.vuid.startsWith('temp-')) {
      try {
        const res = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
        const fetched = res.data || [];
        setAllocations(fetched);
        setAllocationsByItem((prev) => ({ ...prev, [item.vuid]: fetched }));
        console.log(`Loaded ${fetched.length} existing allocations for item ${item.item_number}:`, fetched);
      } catch (err) {
        console.error('Error fetching allocations:', err);
        // If fetch fails, try to use cached allocations
        const cached = allocationsByItem[item.vuid] || [];
        setAllocations(cached);
        console.log(`Using cached allocations for item ${item.item_number}:`, cached);
      }
    } else {
      // For temp items, use cached allocations
      const cached = allocationsByItem[item.vuid] || [];
      setAllocations(cached);
      console.log(`Using cached allocations for temp item ${item.item_number}:`, cached);
    }
    
    // Fetch project budget lines to filter allocation options
    if (projectVuid) {
      axios
        .get(`${baseURL}/api/projects/${projectVuid}/budget-lines`)
        .then((res) => {
          setProjectBudgetLines(res.data || []);
        })
        .catch((err) => {
          console.warn('Failed to fetch project budget lines:', err);
          setProjectBudgetLines([]);
        });
    }
  };

  // Handle add allocation
  const handleAddAllocation = () => {
    if (newAllocation.cost_code_vuid && newAllocation.cost_type_vuid) {
      const allocation = {
        ...newAllocation,
        vuid: `temp-allocation-${Date.now()}`,
        contract_item_vuid: selectedItemForAllocations.vuid
      };
      setAllocations([...allocations, allocation]);
      setNewAllocation({
        cost_code_vuid: '',
        cost_type_vuid: '',
      });
      setShowAddAllocationForm(false);
    }
  };

  // Handle delete allocation
  const handleDeleteAllocation = async (allocationVuid) => {
    try {
      // If this is an existing allocation (has a real vuid), delete it from the database
      if (allocationVuid && !allocationVuid.startsWith('temp-')) {
        await axios.delete(`${baseURL}/api/project-contract-items/allocations/${allocationVuid}`);
        console.log(`Deleted allocation ${allocationVuid} from database`);
      }
      
      // Remove from local state
      setAllocations(allocations.filter(a => a.vuid !== allocationVuid));
      
      // Refresh allocation status to update the alert
      const projectVuid = selectedContract?.project_vuid;
      if (projectVuid) {
        await fetchAllocationStatus(projectVuid);
      }
    } catch (error) {
      console.error('Error deleting allocation:', error);
      setError('Error deleting allocation: ' + error.message);
    }
  };

  // Handle save allocations
  const handleSaveAllocations = async () => {
    try {
      const item = selectedItemForAllocations;
      if (!item) return;
      
      console.log('=== SAVING ALLOCATIONS ===');
      console.log('Item:', item.item_number, 'vuid:', item.vuid);
      console.log('Allocations to save:', allocations);
      
      if (item.vuid && !item.vuid.startsWith('temp-')) {
        // Validate all allocations before making any changes
        console.log('Validating allocations before save...');
        for (const alloc of allocations) {
          const validation = validateCostCodeTypeCombination(alloc.cost_code_vuid, alloc.cost_type_vuid, item.vuid);
          if (!validation.isValid) {
            throw new Error(`Allocation validation failed: ${validation.message}`);
          }
        }
        console.log('All allocations validated successfully');
        
        // Get current allocations from backend to compare
        const existingRes = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
        const existingAllocs = existingRes.data || [];
        console.log('Current allocations in database:', existingAllocs);
        
        // Check if allocations actually changed to avoid unnecessary operations
        const allocationsChanged = allocations.length !== existingAllocs.length || 
          allocations.some((alloc, index) => {
            const existing = existingAllocs[index];
            return !existing || 
                   existing.cost_code_vuid !== alloc.cost_code_vuid || 
                   existing.cost_type_vuid !== alloc.cost_type_vuid;
          });
        
        if (!allocationsChanged) {
          console.log('No changes detected, skipping save operation');
          setShowAllocationsModal(false);
          setSuccessMessage('No changes to save');
          setTimeout(() => setSuccessMessage(''), 3000);
          return;
        }
        
        // Only create new allocations (deletions are handled by handleDeleteAllocation)
        console.log(`Creating ${allocations.length} new allocations...`);
        const createdAllocs = [];
        for (const alloc of allocations) {
          // Only create if it's a new allocation (doesn't have a real vuid yet)
          if (!alloc.vuid || alloc.vuid.startsWith('temp-')) {
            console.log(`Creating new allocation:`, alloc);
            const response = await axios.post(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`, {
              cost_code_vuid: alloc.cost_code_vuid,
              cost_type_vuid: alloc.cost_type_vuid,
              notes: alloc.notes || ''
            });
            createdAllocs.push(response.data);
            console.log(`Allocation created successfully:`, response.data);
          } else {
            // Keep existing allocation
            createdAllocs.push(alloc);
            console.log(`Keeping existing allocation:`, alloc.vuid);
          }
        }
        
        // Update local state with the newly created allocations
        console.log(`Final allocations for item ${item.item_number}:`, createdAllocs);
        setAllocations(createdAllocs);
        setAllocationsByItem(prev => ({ ...prev, [item.vuid]: createdAllocs }));
        
        // Refresh the contract items to ensure consistency
        await fetchContractItems(selectedContract.vuid);
        
        // Refresh allocation status to update the alert
        const projectVuid = selectedContract?.project_vuid;
        if (projectVuid) {
          await fetchAllocationStatus(projectVuid);
        }
        
      } else {
        // Cache locally for temp items
        console.log('Caching allocations for temp item:', item.item_number);
        setAllocationsByItem(prev => ({ ...prev, [item.vuid]: allocations }));
      }
      
      setShowAllocationsModal(false);
      setSuccessMessage('Allocations saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error in handleSaveAllocations:', err);
      setError('Error saving allocations: ' + err.message);
    }
  };

  // Helper function to get all allocations for a contract
  const getAllContractAllocations = (contract) => {
    if (!contract?.items) return [];
    return contract.items.flatMap(item => allocationsByItem[item.vuid] || []);
  };

  // Helper function to validate cost code cost type combination uniqueness
  const validateCostCodeTypeCombination = (costCodeVuid, costTypeVuid, currentItemVuid = null) => {
    if (!costCodeVuid || !costTypeVuid) return { isValid: true, message: '' };
    
    console.log('=== VALIDATION DEBUG ===');
    console.log('Validating:', { costCodeVuid, costTypeVuid, currentItemVuid });
    console.log('selectedContract:', selectedContract);
    console.log('contractItems:', contractItems);
    
    // Check against existing contract items first (more reliable)
    // IMPORTANT: Only check against items that are NOT temp items and NOT the current item being validated
    const existingItems = contractItems.filter(item => 
      item.vuid && 
      !item.vuid.startsWith('temp-') && 
      item.vuid !== currentItemVuid &&
      item.cost_code_vuid && 
      item.cost_type_vuid
    );
    
    console.log('Existing items to check:', existingItems);
    
    // Check if any existing item has the same cost code cost type combination
    const duplicateItem = existingItems.find(item => 
      item.cost_code_vuid === costCodeVuid && item.cost_type_vuid === costTypeVuid
    );
    
    if (duplicateItem) {
      const costCode = costCodes.find(c => c.vuid === costCodeVuid);
      const costType = costTypes.find(t => t.vuid === costTypeVuid);
      const codeName = costCode ? costCode.code : costCodeVuid;
      const typeName = costType ? costType.cost_type : costTypeVuid;
      
      console.log('DUPLICATE FOUND:', { duplicateItem, codeName, typeName });
      
      return {
        isValid: false,
        message: `Cost code ${codeName} with cost type ${typeName} is already used by item ${duplicateItem.item_number}.`
      };
    }
    
    // Also check against allocations as a backup
    const allContractAllocations = getAllContractAllocations(selectedContract);
    console.log('All contract allocations:', allContractAllocations);
    
    const duplicateAllocation = allContractAllocations.find(allocation => {
      // Skip the current item if we're editing
      if (currentItemVuid && allocation.contract_item_vuid === currentItemVuid) {
        return false;
      }
      
      return allocation.cost_code_vuid === costCodeVuid && 
             allocation.cost_type_vuid === costTypeVuid;
    });
    
    if (duplicateAllocation) {
      const costCode = costCodes.find(c => c.vuid === costCodeVuid);
      const costType = costTypes.find(t => t.vuid === costTypeVuid);
      const codeName = costCode ? costCode.code : costCodeVuid;
      const typeName = costType ? costType.cost_type : costTypeVuid;
      
      console.log('DUPLICATE ALLOCATION FOUND:', { duplicateAllocation, codeName, typeName });
      
      return {
        isValid: false,
        message: `Cost code ${codeName} with cost type ${typeName} is already allocated to another contract item.`
      };
    }
    
    console.log('Validation passed - no duplicates found');
    return { isValid: true, message: '' };
  };

  // Helper function to get all available cost codes (global project-specific)
  const getAllCostCodes = () => {
    return [...costCodes, ...projectCostCodes];
  };

  // Helper function to get the display value for cost code input
  const getCostCodeInputValue = (item) => {
    if (!item.cost_code_vuid) return '';
    if (typeof item.cost_code_vuid === 'string' && item.cost_code_vuid.length !== 36) {
      return item.cost_code_vuid;
    }
    const costCode = getAllCostCodes().find((c) => c.vuid === item.cost_code_vuid);
    return costCode ? costCode.code : '';
  };

  /** Global cost codes from API often omit status; treat missing status as active. */
  const isCostCodeSelectable = (c) => !c.status || c.status === 'active';
  const isCostTypeSelectable = (t) => !t.status || t.status === 'active';

  // Function to create project-specific cost code if it doesn't exist (similar to budget entries)
  const createProjectCostCodeIfNeeded = async (costCodeValue) => {
    if (!projectSettings?.allow_project_cost_codes) {
      return costCodeValue; // Return as-is for global mode
    }

    // Check if this cost code already exists as a project cost code
    const existingProjectCostCode = projectCostCodes.find(
      cc => cc.is_project_specific && cc.code === costCodeValue
    );

    if (existingProjectCostCode) {
      return existingProjectCostCode.vuid; // Return existing VUID
    }

    // Create new project-specific cost code
    try {
      const response = await axios.post(`${baseURL}/api/projects/${selectedContract.project_vuid}/cost-codes`, {
        code: costCodeValue,
        description: `Project-specific cost code: ${costCodeValue}`
      });
      
      // Add to local state
      const newCostCode = response.data;
      setProjectCostCodes(prev => [...prev, newCostCode]);
      
      return newCostCode.vuid;
    } catch (error) {
      console.error('Error creating project cost code:', error);
      throw error;
    }
  };

  // Helper functions for smart dropdowns
  const getFilteredProjects = () => {
    if (!projectSearchTerm) return projects;
    return projects.filter(project => 
      project.project_name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      project.project_number.toLowerCase().includes(projectSearchTerm.toLowerCase())
    );
  };

  const getFilteredCustomers = () => {
    if (!customerSearchTerm) return customers;
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      (customer.customer_name || '').toLowerCase().includes(searchLower) ||
      (customer.customer_number || '').toLowerCase().includes(searchLower)
    );
  };

  // Helper functions to get display values for cost code and cost type columns
  const getCostCodeDisplay = (item) => {
    const itemAllocs = allocationsByItem[item.vuid] || [];
    console.log('getCostCodeDisplay for item:', item.item_number, 'vuid:', item.vuid, 'allocations:', itemAllocs, 'cost_code_vuid:', item.cost_code_vuid);
    
    // If there are multiple allocations, always show "Multiple"
    if (itemAllocs.length > 1) {
      return 'Multiple';
    }
    
    // If there's exactly one allocation, show that allocation's cost code
    if (itemAllocs.length === 1) {
      // Check both global and project-specific cost codes
      let costCode = costCodes.find(c => c.vuid === itemAllocs[0].cost_code_vuid);
      if (!costCode) {
        costCode = projectCostCodes.find(c => c.vuid === itemAllocs[0].cost_code_vuid);
      }
      return costCode ? `${costCode.code} - ${costCode.description}` : 'Unknown';
    }
    
    // If no allocations, show the item's own cost code (if any)
    if (item.cost_code_vuid) {
      // Check both global and project-specific cost codes
      let costCode = costCodes.find(c => c.vuid === item.cost_code_vuid);
      if (!costCode) {
        costCode = projectCostCodes.find(c => c.vuid === item.cost_code_vuid);
      }
      return costCode ? `${costCode.code} - ${costCode.description}` : 'Unknown';
    }
    
    return '';
  };

  const getCostTypeDisplay = (item) => {
    const itemAllocs = allocationsByItem[item.vuid] || [];
    console.log('getCostTypeDisplay for item:', item.item_number, 'vuid:', item.vuid, 'allocations:', itemAllocs, 'cost_type_vuid:', item.cost_type_vuid);
    
    // If there are multiple allocations, always show "Multiple"
    if (itemAllocs.length > 1) {
      return 'Multiple';
    }
    
    // If there's exactly one allocation, show that allocation's cost type
    if (itemAllocs.length === 1) {
      const costType = costTypes.find(t => t.vuid === itemAllocs[0].cost_type_vuid);
      return costType ? costType.cost_type : 'Unknown';
    }
    
    // If no allocations, show the item's own cost type (if any)
    if (item.cost_type_vuid) {
      const costType = costTypes.find(t => t.vuid === item.cost_type_vuid);
      return costType ? costType.cost_type : 'Unknown';
    }
    
    return '';
  };

  // Helper function to get markup from fee settings
  const getMarkupFromFeeSettings = (costCodeVuid, costTypeVuid) => {
    if (!costCodeVuid || !costTypeVuid || feeLevel !== 'contract_item') {
      return null;
    }
    
    const feeSetting = feeSettings.find(
      setting => setting.cost_code_vuid === costCodeVuid && 
                 setting.cost_type_vuid === costTypeVuid
    );
    
    return feeSetting ? (feeSetting.fee_percentage || 0) : null;
  };

  // Helper function to recalculate total_amount based on markup change
  // If we have an old markup and new markup, recalculate total_amount
  // Formula: new_total = (old_total / (1 + old_markup/100)) * (1 + new_markup/100)
  const recalculateTotalAmountWithNewMarkup = (currentTotal, oldMarkup, newMarkup) => {
    if (!currentTotal || currentTotal <= 0) {
      return currentTotal;
    }
    
    const oldMarkupValue = parseFloat(oldMarkup || 0);
    const newMarkupValue = parseFloat(newMarkup || 0);
    
    // If markup hasn't changed, return current total
    if (oldMarkupValue === newMarkupValue) {
      return currentTotal;
    }
    
    // Calculate base amount (what total_amount would be without markup)
    // If old markup was applied: base = total / (1 + old_markup/100)
    const baseAmount = oldMarkupValue > 0 
      ? parseFloat(currentTotal) / (1 + (oldMarkupValue / 100))
      : parseFloat(currentTotal);
    
    // Apply new markup: new_total = base * (1 + new_markup/100)
    const newTotal = baseAmount * (1 + (newMarkupValue / 100));
    return Math.round(newTotal * 100) / 100; // Round to 2 decimals
  };

  // Handle view items
  const handleViewItems = async (contract) => {
    console.log('🔧 handleViewItems called for contract:', contract.contract_number);
    setSelectedContract(contract);
    setShowItemsForm(true);
    setShowCreateForm(false);
    
    // Reset percentage adjustment states when viewing a different contract
    setAdjustmentPercentage(0);
    setShowPercentageAdjustment(false);
    setOriginalAmounts({});
    
    // Clear existing project cost codes first
    setProjectCostCodes([]);
    
    // Fetch project-specific cost codes for this contract's project
    try {
      console.log('Fetching project cost codes...');
      const projectCostCodesRes = await axios.get(`${baseURL}/api/projects/${contract.project_vuid}/cost-codes`);
      const projectCostCodesData = projectCostCodesRes.data || [];
      setProjectCostCodes(projectCostCodesData);
      console.log(`Fetched ${projectCostCodesData.length} project cost codes for contract ${contract.contract_number}`);
      console.log('First 3 project cost codes:', projectCostCodesData.slice(0, 3).map(c => ({ vuid: c.vuid, code: c.code, description: c.description })));
    } catch (e) {
      console.error('Failed to fetch project cost codes:', e);
      setProjectCostCodes([]);
    }
    
    // Fetch project budget lines for this contract's project
    try {
      const budgetLinesRes = await axios.get(`${baseURL}/api/projects/${contract.project_vuid}/budget-lines`);
      setProjectBudgetLines(budgetLinesRes.data || []);
      console.log(`Fetched ${budgetLinesRes.data?.length || 0} budget lines for contract ${contract.contract_number}`);
    } catch (e) {
      console.warn('Failed to fetch project budget lines:', e);
      setProjectBudgetLines([]);
    }
    
    // Fetch project settings
    try {
      const settingsRes = await axios.get(`${baseURL}/api/projects/${contract.project_vuid}/settings`);
      setProjectSettings(settingsRes.data || {});
      console.log(`Fetched project settings:`, settingsRes.data);
    } catch (e) {
      console.warn('Failed to fetch project settings:', e);
      setProjectSettings({});
    }
    
    // Fetch fee level
    let currentFeeLevel = 'contract_item';
    try {
      const feeLevelRes = await axios.get(`${baseURL}/api/contracts/${contract.vuid}/fee-level`);
      currentFeeLevel = feeLevelRes.data?.fee_level || 'contract_item';
      setFeeLevel(currentFeeLevel);
      console.log(`Fetched fee level:`, currentFeeLevel);
    } catch (e) {
      console.warn('Failed to fetch fee level:', e);
      setFeeLevel('contract_item'); // Default
    }
    
    // Fetch fee settings (only needed if fee level is contract_item)
    let currentFeeSettings = [];
    if (currentFeeLevel === 'contract_item') {
      try {
        const feeSettingsRes = await axios.get(`${baseURL}/api/contracts/${contract.vuid}/fee-settings`);
        currentFeeSettings = feeSettingsRes.data || [];
        setFeeSettings(currentFeeSettings);
        console.log(`Fetched fee settings:`, currentFeeSettings.length, 'settings');
      } catch (e) {
        console.warn('Failed to fetch fee settings:', e);
        setFeeSettings([]);
      }
    } else {
      setFeeSettings([]);
    }
    
    fetchContractItems(contract.vuid, currentFeeLevel, currentFeeSettings);
  };

  // Handle hide items
  const handleHideItems = () => {
    setSelectedContract(null);
    setShowItemsForm(false);
    setContractItems([]);
  };

  // Fetch contract items for a specific contract
  const fetchContractItems = async (contractVuid, feeLevelParam = null, feeSettingsParam = null) => {
    try {
      console.log('🔍 fetchContractItems called for contract:', contractVuid);
      const response = await axios.get(`${baseURL}/api/project-contracts/${contractVuid}/items`);
      console.log('📦 API response status:', response.status);
      console.log('📦 API response data:', response.data);
      console.log('📊 Number of items received:', response.data?.length || 0);
      
      // Use passed parameters or state (parameters take precedence for immediate updates)
      const effectiveFeeLevel = feeLevelParam !== null ? feeLevelParam : feeLevel;
      const effectiveFeeSettings = feeSettingsParam !== null ? feeSettingsParam : feeSettings;
      
      // If fee level is contract_item, update markup and recalculate total_amount for each item
      let itemsToSet = response.data || [];
      if (effectiveFeeLevel === 'contract_item' && itemsToSet.length > 0 && effectiveFeeSettings.length > 0) {
        // Helper to get markup from fee settings (using passed parameter)
        const getMarkupFromSettings = (costCodeVuid, costTypeVuid) => {
          const feeSetting = effectiveFeeSettings.find(
            setting => setting.cost_code_vuid === costCodeVuid && 
                       setting.cost_type_vuid === costTypeVuid
          );
          return feeSetting ? (feeSetting.fee_percentage || 0) : null;
        };
        
        itemsToSet = itemsToSet.map(item => {
          if (!item.cost_code_vuid || !item.cost_type_vuid) {
            return item;
          }
          
          const markupFromSettings = getMarkupFromSettings(item.cost_code_vuid, item.cost_type_vuid);
          if (markupFromSettings !== null) {
            const oldMarkup = item.markup_percentage ?? 0;
            const newMarkup = markupFromSettings;
            const currentTotal = parseFloat(item.total_amount || 0);
            const recalculatedTotal = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
            
            return {
              ...item,
              markup_percentage: newMarkup,
              total_amount: recalculatedTotal
            };
          }
          return item;
        });
      }
      
      // Log each item to see what we're getting
      if (itemsToSet && itemsToSet.length > 0) {
        itemsToSet.forEach((item, index) => {
          console.log(`✅ Item ${index}:`, {
            vuid: item.vuid,
            item_number: item.item_number,
            description: item.description,
            total_amount: item.total_amount,
            cost_code_vuid: item.cost_code_vuid,
            cost_type_vuid: item.cost_type_vuid
          });
        });
      } else {
        console.warn('⚠️ No items in response data');
      }
      
      console.log('💾 Setting contractItems state with', itemsToSet.length, 'items');
      setContractItems(itemsToSet);
      console.log('✅ contractItems state updated with:', itemsToSet.length, 'items');
      
      // If fee level is contract_item and we recalculated items, save them back to database
      // This ensures the database has the correct total_amount values with markup applied
      if (effectiveFeeLevel === 'contract_item' && itemsToSet.length > 0 && effectiveFeeSettings.length > 0) {
        // Compare with original response data to see which items actually changed
        const originalItemsMap = new Map((response.data || []).map(item => [item.vuid, item]));
        
        const itemsNeedingUpdate = itemsToSet.filter(item => {
          // Only update items that have cost code/type and exist in database (not temp items)
          if (!item.cost_code_vuid || !item.cost_type_vuid || !item.vuid || item.vuid.startsWith('temp-')) {
            return false;
          }
          
          // Check if values actually changed
          const originalItem = originalItemsMap.get(item.vuid);
          if (!originalItem) {
            return false; // Item not in original data, skip
          }
          
          const originalTotal = parseFloat(originalItem.total_amount || 0);
          const newTotal = parseFloat(item.total_amount || 0);
          const originalMarkup = parseFloat(originalItem.markup_percentage || 0);
          const newMarkup = parseFloat(item.markup_percentage || 0);
          
          // Update if total_amount or markup_percentage changed
          return Math.abs(originalTotal - newTotal) > 0.01 || Math.abs(originalMarkup - newMarkup) > 0.01;
        });
        
        if (itemsNeedingUpdate.length > 0) {
          console.log(`🔄 Saving ${itemsNeedingUpdate.length} items with recalculated markup to database...`);
          // Save items asynchronously (don't block UI)
          Promise.all(itemsNeedingUpdate.map(async (item) => {
            try {
              await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
                total_amount: item.total_amount,
                markup_percentage: item.markup_percentage
              });
              console.log(`✅ Updated item ${item.item_number}: total_amount=${item.total_amount}, markup=${item.markup_percentage}%`);
            } catch (err) {
              console.warn(`Failed to update item ${item.item_number}:`, err);
            }
          })).then(() => {
            console.log(`✅ All ${itemsNeedingUpdate.length} items updated with recalculated markup values`);
          });
        } else {
          console.log('ℹ️ No items need updating (values already match)');
        }
      }
      
      // Also fetch change order lines for this contract
      await fetchChangeOrderLines(contractVuid);
      
      // Fetch allocations for each existing item (use itemsToSet which may have recalculated values)
      const itemsWithVuid = itemsToSet?.filter(item => item.vuid && !item.vuid.startsWith('temp-')) || [];
      for (const item of itemsWithVuid) {
        try {
          const allocResponse = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
          const itemAllocs = allocResponse.data || [];
          setAllocationsByItem(prev => ({ ...prev, [item.vuid]: itemAllocs }));
        } catch (err) {
          console.error(`Error fetching allocations for item ${item.vuid}:`, err);
          setAllocationsByItem(prev => ({ ...prev, [item.vuid]: [] }));
        }
      }
      
      // Update contract amount based on fetched items (use itemsToSet which may have recalculated totals)
      if (itemsToSet && itemsToSet.length > 0) {
        // Sum all item amounts and round to 2 decimal places to avoid floating point precision issues
        const total = Math.round(itemsToSet.reduce((sum, item) => sum + (parseFloat(item.total_amount || 0) || 0), 0) * 100) / 100;
        
        // Update local contract state
        setContracts(prev => prev.map(c => 
          c.vuid === contractVuid ? { ...c, contract_amount: total } : c
        ));
        
        // Update selected contract if it's the current one
        if (selectedContract && selectedContract.vuid === contractVuid) {
          setSelectedContract(prev => prev ? { ...prev, contract_amount: total } : null);
        }
        
        // Update the contract amount in the backend (non-blocking)
        // This ensures the database has the correct calculated amount from items
        // Don't call fetchData() here as it may clear the selected contract and items
        axios.put(`${baseURL}/api/project-contracts/${contractVuid}`, {
          contract_amount: total
        }).then(() => {
          console.log(`✅ Contract amount updated to $${total.toFixed(2)} for contract ${contractVuid}`);
          // Just update the local contracts list without refetching everything
          // This preserves the selected contract and items
        }).catch(err => {
          console.warn('⚠️ Error updating contract amount:', err);
        });
      }
    } catch (err) {
      console.error('❌ Error fetching contract items:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      // Don't clear items on error - keep existing items visible
      // Only clear if this is a 404 (contract not found) or 401 (unauthorized)
      if (err.response?.status === 404 || err.response?.status === 401) {
        console.warn('⚠️ Clearing items due to 404/401 error');
        setContractItems([]);
      } else {
        console.warn('⚠️ Keeping existing items despite fetch error');
      }
    }
  };

  // Fetch change order lines for a specific contract
  const fetchChangeOrderLines = async (contractVuid) => {
    try {
      console.log('fetchChangeOrderLines called for contract:', contractVuid);
      
      // First get all external change orders for this contract
      const changeOrdersResponse = await axios.get(`${baseURL}/api/external-change-orders?contract_vuid=${contractVuid}`);
      const changeOrders = changeOrdersResponse.data || [];
      setExternalChangeOrders(changeOrders);
      
      console.log('Found change orders:', changeOrders.length);
      
      // Then get all change order lines for these change orders
      const allChangeOrderLines = [];
      for (const changeOrder of changeOrders) {
        try {
          const linesResponse = await axios.get(`${baseURL}/api/external-change-orders/${changeOrder.vuid}/lines`);
          const lines = linesResponse.data || [];
          // Add change order info to each line
          const linesWithChangeOrder = lines.map(line => ({
            ...line,
            change_order_number: changeOrder.change_order_number,
            change_order_date: changeOrder.change_order_date,
            change_order_status: changeOrder.status
          }));
          allChangeOrderLines.push(...linesWithChangeOrder);
        } catch (err) {
          console.error(`Error fetching lines for change order ${changeOrder.vuid}:`, err);
        }
      }
      
      setChangeOrderLines(allChangeOrderLines);
      console.log('Total change order lines:', allChangeOrderLines.length);
      
    } catch (err) {
      console.error('Error fetching change order lines:', err);
      setChangeOrderLines([]);
      setExternalChangeOrders([]);
    }
  };

  // Calculate change order amount for a specific contract line
  const getChangeOrderAmount = (contractLine) => {
    if (!contractLine.cost_code_vuid || !contractLine.cost_type_vuid) {
      return + 0;
    }
    
    const matchingChangeOrderLines = changeOrderLines.filter(changeLine => 
      changeLine.cost_code_vuid === contractLine.cost_code_vuid &&
      changeLine.cost_type_vuid === contractLine.cost_type_vuid
    );
    
    const totalChangeAmount = matchingChangeOrderLines.reduce((sum, changeLine) => {
      return sum + (parseFloat(changeLine.contract_amount_change || 0) || 0);
    }, 0);
    
    return totalChangeAmount;
  };

  // Reset forms
  const resetForm = () => {
    setFormData({
      project_vuid: '',
      contract_number: '',
      contract_date: '',
      customer_vuid: '',
      accounting_period_vuid: '',
      contract_type: CONTRACT_TYPES[0].value,
      status: 'active',
      description: '',
      total_amount: '',
      currency: 'USD',
      payment_terms: '',
      delivery_terms: '',
      warranty_terms: '',
      start_date: '',
      end_date: '',
      delivery_date: '',
      notes: ''
    });
    setCreateItemsFromBudget(true);
    setBudgetIncreasePercentage(0);
    
    // Clear search terms
    setProjectSearchTerm('');
    setCustomerSearchTerm('');
    setEditingContract(null);
    setShowCreateForm(false); // Hide the create form
    setShowItemsForm(false);
    setEditingItem(null);
    setSuccessMessage(''); // Clear success message
  };

  const resetItemForm = () => {
    setItemFormData({
      item_number: '',
      description: '',
      quantity: 1,
      unit_of_measure: 'EA',
      unit_price: '',
      total_amount: '',
      markup_percentage: budgetIncreasePercentage,
      status: 'active',
      cost_code_vuid: null,
      cost_type_vuid: null,
      specifications: '',
      delivery_location: '',
      delivery_date: '',
      warranty_info: '',
      notes: '',
      contract_vuid: ''
    });
    setNewItemData({
      item_number: '',
      cost_code_vuid: '',
      cost_type_vuid: '',
      description: '',
      total_amount: '',
      markup_percentage: budgetIncreasePercentage,
      notes: ''
    });
    setEditingItem(null);
    setShowItemsForm(false);
  };

  const handleShowCreateForm = () => {
    setShowCreateForm(true);
    setEditingContract(null);
    // Reset form data but don't hide the form
    setFormData({
      project_vuid: '',
      contract_number: '',
      contract_date: '',
      customer_vuid: '',
      accounting_period_vuid: '',
      contract_type: CONTRACT_TYPES[0].value,
      status: 'active',
      description: '',
      total_amount: '',
      currency: 'USD',
      payment_terms: '',
      delivery_terms: '',
      warranty_terms: '',
      start_date: '',
      end_date: '',
      delivery_date: '',
      notes: ''
    });
    setSuccessMessage('');
    
    // Scroll to and focus on the contract form header when creating a new contract
    setTimeout(() => {
      if (contractFormHeaderRef.current) {
        contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus on the first input field (project selector)
        setTimeout(() => {
          const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
          if (firstInput) {
            firstInput.focus();
          }
        }, 100);
      }
    }, 100);
  };

  const handleShowItemsForm = () => {
    setShowItemsForm(true);
    setEditingItem(null);
    resetItemForm();
  };

  const closeItemsForm = () => {
    setShowItemsForm(false);
    setSelectedContract(null);
    setContractItems([]);
    setEditingItem(null);
    resetItemForm();
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingContract(null);
    resetForm();
  };

  // Refresh contract amounts for all contracts
  const refreshContractAmounts = async () => {
    try {
      for (const contract of contracts) {
        if (contract.vuid) {
          try {
            const itemsResponse = await axios.get(`${baseURL}/api/project-contracts/${contract.vuid}/items`);
            const items = itemsResponse.data || [];
            
            if (items.length > 0) {
              const total = items.reduce((sum, item) => sum + (parseFloat(item.total_amount || 0) || 0), 0);
              
              // Update local contract state
              setContracts(prev => prev.map(c => 
                c.vuid === contract.vuid ? { ...c, contract_amount: total } : c
              ));
              
              // Update backend (non-blocking)
              axios.put(`${baseURL}/api/project-contracts/${contract.vuid}`, {
                contract_amount: total
              }).catch(err => {
                console.warn(`Failed to update backend for contract ${contract.contract_number}:`, err);
              });
            }
          } catch (err) {
            console.warn(`Error refreshing amount for contract ${contract.contract_number}:`, err);
          }
        }
      }
    } catch (err) {
      console.warn('Error refreshing contract amounts:', err);
    }
  };

  // Handle going back to main contract view after adding items
  const handleDoneAddingItems = () => {
    setShowItemsForm(false);
    setSelectedContract(null);
    setShowCreateForm(false);
    setEditingItem(null);
    setEditingContract(null);
    setSuccessMessage(''); // Clear success message
    // Reset the item form data
    setItemFormData({
      item_number: '',
      description: '',
      quantity: 1,
      unit_of_measure: 'EA',
      unit_price: '',
      total_amount: '',
      status: 'active',
      cost_code_vuid: null,
      cost_type_vuid: null,
      specifications: '',
      delivery_location: '',
      delivery_date: '',
      warranty_info: '',
      notes: '',
      contract_vuid: ''
    });
  };

  // Handle edit contract
  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setFormData({
      project_vuid: contract.project_vuid || '',
      contract_number: contract.contract_number || '',
      contract_date: contract.contract_date || '',
              customer_vuid: contract.customer_vuid || '',
        accounting_period_vuid: contract.accounting_period_vuid || '',
      contract_type: contract.contract_type || CONTRACT_TYPES[0].value,
      status: contract.status || 'active',
      description: contract.description || '',
      total_amount: contract.total_amount || '',
      currency: contract.currency || 'USD',
      payment_terms: contract.payment_terms || '',
      delivery_terms: contract.delivery_terms || '',
      warranty_terms: contract.warranty_terms || '',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      delivery_date: contract.delivery_date || '',
      notes: contract.notes || ''
    });
    setShowCreateForm(true);
  };

  // Handle delete contract
  const handleDeleteContract = async (contract) => {
    if (window.confirm('Are you sure you want to delete this contract? This will also delete all associated contract items and allocations.')) {
      try {
        await axios.delete(`${baseURL}/api/project-contracts/${contract.vuid}`);
        setContracts(prev => prev.filter(c => c.vuid !== contract.vuid));
        
        // If we were viewing items for this contract, clear the view
        if (selectedContract && selectedContract.vuid === contract.vuid) {
          setSelectedContract(null);
          setShowItemsForm(false);
          setContractItems([]);
          setAllocationsByItem({});
        }
        
        setSuccessMessage('Contract and all associated items deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error deleting contract:', err);
        setError('Error deleting contract: ' + err.message);
      }
    }
  };

  // Fetch integrations
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      
      // Filter integrations to only show those that have 'project_contracts' enabled or are Procore
      const filteredIntegrations = response.data.filter(integration => 
        integration.status === 'active' && 
        (integration.integration_type === 'procore' || 
         (integration.enabled_objects && integration.enabled_objects.project_contracts === true))
      );
      
      setIntegrations(filteredIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  // Handle integration modal
  const handleIntegrationModal = (contract) => {
    setSelectedContractForIntegration(contract);
    setSelectedIntegration(null);
    setShowIntegrationModal(true);
    fetchIntegrations();
  };

  // Handle send to integration
  const handleSendToIntegration = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    if (!selectedContractForIntegration) {
      alert('No contract selected');
      return;
    }

    setSendingToIntegration(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/send-contract`,
        { contract_vuid: selectedContractForIntegration.vuid },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const message = response.data.message || 
          `Contract "${selectedContractForIntegration.contract_number}" sent to ${selectedIntegration.integration_name} successfully!`;
        
        let fullMessage = message;
        if (response.data.line_items_created !== undefined) {
          fullMessage += `\n\nLine Items: ${response.data.line_items_created} created`;
          if (response.data.line_items_failed > 0) {
            fullMessage += `, ${response.data.line_items_failed} failed`;
          }
        }
        
        if (response.data.errors && response.data.errors.length > 0) {
          fullMessage += `\n\nErrors:\n${response.data.errors.join('\n')}`;
        }
        
        alert(fullMessage);
        
        // Close the modal
        setShowIntegrationModal(false);
        setSelectedContractForIntegration(null);
        setSelectedIntegration(null);
      } else {
        alert(`Error: ${response.data.error || 'Failed to send contract'}`);
      }
    } catch (error) {
      console.error('Error sending contract to integration:', error);
      const errorMsg = error.response?.data?.error || error.message || 'Failed to send contract to integration';
      alert(`Error: ${errorMsg}`);
    } finally {
      setSendingToIntegration(false);
    }
  };

  // Handle retrieve from integration
  const handleRetrieveFromIntegration = async (integration) => {
    try {
      // Here you would implement the logic to retrieve contracts from the selected integration
      console.log(`Retrieving contracts from integration ${integration.integration_name}`);
      
      // For now, just show a success message
      alert(`Contracts retrieved from ${integration.integration_name} successfully!`);
      
      // Close the modal
      setShowRetrieveModal(false);
    } catch (error) {
      console.error('Error retrieving contracts from integration:', error);
      alert('Error retrieving contracts from integration');
    }
  };

  // Handle retrieve modal
  const handleRetrieveModal = () => {
    setShowRetrieveModal(true);
  };



  // Pagination
  const totalPages = Math.ceil(filteredContracts.length / contractsPerPage);
  const startIndex = (currentPage - 1) * contractsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, startIndex + contractsPerPage);

  // Excel-style form functions for contract items
  // Handle cost code/type changes for existing items
  const handleCostCodeTypeChange = async (itemVuid, newCostCodeVuid, newCostTypeVuid) => {
    console.log('=== HANDLING COST CODE/TYPE CHANGE ===');
    console.log('Item:', itemVuid, 'New cost code:', newCostCodeVuid, 'New cost type:', newCostTypeVuid);
    
    const item = contractItems.find(i => i.vuid === itemVuid);
    if (!item) {
      console.log('Item not found:', itemVuid);
      return;
    }
    
    // If both cost code and type are provided, create new allocation
    if (newCostCodeVuid && newCostTypeVuid) {
      console.log('Creating new allocation for changed cost code/type');
      
      // Validate the new combination
      const validation = validateCostCodeTypeCombination(newCostCodeVuid, newCostTypeVuid, itemVuid);
      if (!validation.isValid) {
        alert(`Cannot change to this combination: ${validation.message}`);
        // Revert the change in the form
        return false; // Signal that the change should be reverted
      }
      
      // Create new allocation
      const newAllocation = {
        vuid: `temp-allocation-${Date.now()}`,
        contract_item_vuid: itemVuid,
        cost_code_vuid: newCostCodeVuid,
        cost_type_vuid: newCostTypeVuid,
        notes: 'Auto-generated allocation'
      };
      
      // Update allocationsByItem
      setAllocationsByItem(prev => ({
        ...prev,
        [itemVuid]: [newAllocation]
      }));
      
      console.log('New allocation created for item:', item.item_number);
      return true; // Signal that the change was successful
    } else {
      // If either is missing, clear allocations
      console.log('Clearing allocations due to missing cost code or type');
      setAllocationsByItem(prev => {
        const updated = { ...prev };
        delete updated[itemVuid];
        return updated;
      });
      return true; // Signal that the change was successful
    }
  };

  const handleAddNewItem = () => {
    console.log('=== ADDING NEW ITEM TO GRID ===');
    console.log('New item data:', newItemData);
    console.log('Has required fields:', {
      item_number: !!newItemData.item_number,
      description: !!newItemData.description,
      total_amount: !!newItemData.total_amount,
      cost_code_vuid: !!newItemData.cost_code_vuid,
      cost_type_vuid: !!newItemData.cost_type_vuid
    });
    
    if (newItemData.item_number && newItemData.description && newItemData.total_amount !== undefined && newItemData.total_amount !== '') {
      // VALIDATE BEFORE ADDING TO GRID
      if (newItemData.cost_code_vuid && newItemData.cost_type_vuid) {
        console.log('Validating new item before adding to grid:', newItemData.item_number, newItemData.cost_code_vuid, newItemData.cost_type_vuid);
        const validation = validateCostCodeTypeCombination(newItemData.cost_code_vuid, newItemData.cost_type_vuid);
        if (!validation.isValid) {
          alert(`Cannot add item: ${validation.message}`);
          return; // Don't add the item
        }
        console.log('Validation passed for new item in grid:', newItemData.item_number);
      } else {
        console.log('WARNING: Item added without cost code or type - will not get auto-allocation');
      }
      
      const newItem = {
        ...newItemData,
        contract_vuid: selectedContract.vuid,
        status: 'active',
        markup_percentage: parseFloat(newItemData.markup_percentage) || 0,
        vuid: `temp-${Date.now()}` // Temporary ID for new items
      };
      
      console.log('Created new item object:', newItem);
      const updatedItems = [...contractItems, newItem];
      setContractItems(updatedItems);
      
      // If cost code and type are provided, create allocation immediately
      if (newItemData.cost_code_vuid && newItemData.cost_type_vuid) {
        console.log('Creating immediate allocation for new item:', newItem.item_number);
        const newAllocation = {
          vuid: `temp-allocation-${Date.now()}`,
          contract_item_vuid: newItem.vuid,
          cost_code_vuid: newItemData.cost_code_vuid,
          cost_type_vuid: newItemData.cost_type_vuid,
          notes: 'Auto-generated allocation'
        };
        
        setAllocationsByItem(prev => ({
          ...prev,
          [newItem.vuid]: [newAllocation]
        }));
        console.log('Immediate allocation created for item:', newItem.item_number);
      }
      
      // Clear the new item form
      setNewItemData({
        item_number: '',
        cost_code_vuid: '',
        cost_type_vuid: '',
        description: '',
        total_amount: '',
        markup_percentage: budgetIncreasePercentage,
        notes: ''
      });
      
      // Focus on the first field of the new item
      setTimeout(() => {
        const newItemInput = document.getElementById('new-item-number');
        if (newItemInput) {
          newItemInput.focus();
          // Also scroll to the new item
          newItemInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    }
  };

  const handleSaveAllItems = async () => {
    try {
      console.log('🚀 SAVE ALL ITEMS BUTTON CLICKED!');
      console.log('Saving all items. Count:', contractItems.length);
      const savedItemsInfo = [];
      
      // Save all new items
      for (const item of contractItems) {
        try {
          console.log('Processing item:', item.item_number, 'vuid:', item.vuid);
          if (item.vuid.startsWith('temp-')) {
            // VALIDATE BEFORE SAVING - this is the key fix!
            if (item.cost_code_vuid && item.cost_type_vuid) {
              console.log('=== VALIDATION BEFORE SAVE ===');
              console.log('Validating new item before save:', item.item_number, item.cost_code_vuid, item.cost_type_vuid);
              console.log('selectedContract at validation:', selectedContract);
              console.log('contractItems at validation:', contractItems);
              
              const validation = validateCostCodeTypeCombination(item.cost_code_vuid, item.cost_type_vuid);
              console.log('Validation result:', validation);
              
              if (!validation.isValid) {
                throw new Error(`Item ${item.item_number}: ${validation.message}`);
              }
              console.log('Validation passed for new item:', item.item_number);
            } else {
              console.log('=== SKIPPING VALIDATION ===');
              console.log('Item missing cost code or type:', {
                item_number: item.item_number,
                cost_code_vuid: item.cost_code_vuid,
                cost_type_vuid: item.cost_type_vuid
              });
            }
            
            // This is a new item, save it
            const res = await axios.post(`${baseURL}/api/project-contract-items`, {
              contract_vuid: item.contract_vuid,
              item_number: item.item_number,
              cost_code_vuid: item.cost_code_vuid || null,
              cost_type_vuid: item.cost_type_vuid || null,
              description: item.description,
              total_amount: item.total_amount || 0,
              notes: item.notes || '',
              status: item.status,
              markup_percentage: parseFloat(item.markup_percentage) || 0
            });
            const savedItem = res.data;
            
            // Track for post-pass verification
            savedItemsInfo.push({
              tempVuid: item.vuid,
              realVuid: savedItem.vuid,
              item_number: item.item_number,
              cost_code_vuid: item.cost_code_vuid,
              cost_type_vuid: item.cost_type_vuid,
            });
            
            // Update the saved item with cost code/type info for consistent display
            const updatedSavedItem = {
              ...savedItem,
              cost_code_vuid: item.cost_code_vuid,
              cost_type_vuid: item.cost_type_vuid
            };
            
            // Create allocation for the newly saved item (validation already passed above)
            console.log('=== ALLOCATION CREATION CHECK ===');
            console.log('Item being processed:', {
              item_number: item.item_number,
              cost_code_vuid: item.cost_code_vuid,
              cost_type_vuid: item.cost_type_vuid,
              has_cost_code: !!item.cost_code_vuid,
              has_cost_type: !!item.cost_type_vuid,
              saved_vuid: savedItem.vuid
            });
            
            if (item.cost_code_vuid && item.cost_type_vuid) {
              
              console.log('=== AUTO-ALLOCATION DEBUG ===');
              console.log('Item details:', {
                item_number: item.item_number,
                cost_code_vuid: item.cost_code_vuid,
                cost_type_vuid: item.cost_type_vuid,
                saved_vuid: savedItem.vuid
              });
              console.log('selectedContract:', selectedContract);
              console.log('contractItems:', contractItems);
              
              try {
                console.log('Creating auto-allocation for item:', item.item_number, 'with cost code:', item.cost_code_vuid, 'and cost type:', item.cost_type_vuid);
                const allocationResponse = await axios.post(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`, {
                  cost_code_vuid: item.cost_code_vuid,
                  cost_type_vuid: item.cost_type_vuid,
                  notes: 'Auto-generated allocation'
                });
                console.log('Auto-allocation created successfully for item:', savedItem.vuid, 'Response:', allocationResponse.data);
                
                // Get the newly created allocation to update state properly
                const allocsRes = await axios.get(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`);
                const savedAllocs = allocsRes.data || [];
                console.log('Retrieved saved allocations for new item:', savedAllocs);
                
                // Update allocationsByItem with the real allocations from the backend
                setAllocationsByItem(prev => {
                  const updated = {
                    ...prev,
                    [savedItem.vuid]: savedAllocs
                  };
                  console.log('Updated allocationsByItem:', updated);
                  return updated;
                });
                
                // IMPORTANT: Refresh the contract items to show the new allocation immediately
                console.log('Refreshing contract items to show new allocation...');
                await fetchContractItems(selectedContract.vuid);
                
              } catch (error) {
                console.error('Error creating auto-allocation:', error);
                console.error('Error details:', {
                  message: error.message,
                  response: error.response?.data,
                  status: error.response?.status
                });
              }
              
            } else {
              console.log('=== SKIPPING AUTO-ALLOCATION ===');
              console.log('Item missing cost code or type:', {
                item_number: item.item_number,
                cost_code_vuid: item.cost_code_vuid,
                cost_type_vuid: item.cost_type_vuid,
                reason: !item.cost_code_vuid ? 'Missing cost code' : 'Missing cost type'
              });
            }
            
            // If we have cached allocations for this temp item, persist them and remap cache to real id
            const cachedAllocs = allocationsByItem[item.vuid];
            if (Array.isArray(cachedAllocs) && cachedAllocs.length > 0) {
              console.log('Persisting cached allocations for temp item:', item.vuid, '→', savedItem.vuid, cachedAllocs);
              
              // Get current allocations to avoid conflicts
              const existingRes = await axios.get(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`);
              const existingAllocs = existingRes.data || [];
              
              // Only create cached allocations if they don't conflict with auto-created ones
              for (const cachedAlloc of cachedAllocs) {
                const isDuplicate = existingAllocs.some(existing => 
                  existing.cost_code_vuid === cachedAlloc.cost_code_vuid && 
                  existing.cost_type_vuid === cachedAlloc.cost_type_vuid
                );
                
                if (!isDuplicate) {
                  console.log('Creating additional cached allocation:', cachedAlloc);
                  await axios.post(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`, {
                    cost_code_vuid: cachedAlloc.cost_code_vuid,
                    cost_type_vuid: cachedAlloc.cost_type_vuid,
                    notes: cachedAlloc.notes || ''
                  });
                } else {
                  console.log('Skipping duplicate cached allocation:', cachedAlloc);
                }
              }
              
              // Refresh allocations after all operations
              const finalAllocsRes = await axios.get(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`);
              const finalAllocs = finalAllocsRes.data || [];
              console.log('Final allocations after processing cached ones:', finalAllocs);
              
              setAllocationsByItem(prev => {
                const next = { ...prev };
                delete next[item.vuid];
                next[savedItem.vuid] = finalAllocs;
                return next;
              });
              
              // Refresh contract items after processing cached allocations
              console.log('Refreshing contract items after processing cached allocations...');
              await fetchContractItems(selectedContract.vuid);
            }
            
            // Update the contractItems state to replace the temp item with the saved item (including cost code/type)
            setContractItems(prev => prev.map(existingItem => 
              existingItem.vuid === item.vuid ? updatedSavedItem : existingItem
            ));
            
          } else {
            // This is an existing item - update it if data has changed
            console.log('Updating existing item:', item.item_number, 'vuid:', item.vuid);
            
            // Update the existing item
            console.log('Sending update request for item:', item.item_number, 'with total_amount:', item.total_amount);
            console.log('Item VUID being used in URL:', item.vuid);
            console.log('Full URL being called:', `${baseURL}/api/project-contract-items/${item.vuid}`);
            const updateResponse = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
              item_number: item.item_number,
              description: item.description,
              quantity: item.quantity,
              unit_of_measure: item.unit_of_measure,
              unit_price: item.unit_price,
              total_amount: item.total_amount,
              status: item.status,
              cost_code_vuid: item.cost_code_vuid,
              cost_type_vuid: item.cost_type_vuid,
              specifications: item.specifications,
              delivery_location: item.delivery_location,
              delivery_date: item.delivery_date,
              warranty_info: item.warranty_info,
              notes: item.notes,
              markup_percentage: parseFloat(item.markup_percentage) || 0
            });
            console.log('Update response:', updateResponse.data);
            console.log('Existing item updated successfully:', item.item_number);
            
            // Update the local state to reflect the saved changes
            setContractItems(prev => prev.map(existingItem => 
              existingItem.vuid === item.vuid ? { ...existingItem, ...updateResponse.data } : existingItem
            ));
            console.log('Local state updated for item:', item.item_number);
            
            // Check if it needs allocations created
            if (item.cost_code_vuid && item.cost_type_vuid) {
              const existingAllocs = allocationsByItem[item.vuid] || [];
              if (existingAllocs.length === 0) {
                // Validate cost code cost type combination uniqueness before creating allocation
                const validation = validateCostCodeTypeCombination(item.cost_code_vuid, item.cost_type_vuid, item.vuid);
                if (!validation.isValid) {
                  throw new Error(`Item ${item.item_number}: ${validation.message}`);
                }
                
                // No allocations exist but cost code/type are set - create allocation
                console.log('Creating missing allocation for existing item:', item.item_number);
                await axios.post(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`, {
                  cost_code_vuid: item.cost_code_vuid,
                  cost_type_vuid: item.cost_type_vuid,
                  notes: 'Auto-generated allocation'
                });
                console.log('Missing allocation created successfully for item:', item.vuid);
                
                // Refresh allocations for this item
                const allocsRes = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
                const savedAllocs = allocsRes.data || [];
                setAllocationsByItem(prev => ({ ...prev, [item.vuid]: savedAllocs }));
              }
            }
          }
        } catch (perItemErr) {
          console.error('Error processing item:', item.item_number, 'vuid:', item.vuid, perItemErr);
        }
      }
      
      // Post-pass: ensure allocations exist for all saved items with code/type
      for (const info of savedItemsInfo) {
        try {
          if (info.cost_code_vuid && info.cost_type_vuid) {
            console.log('Post-pass ensure allocation for item:', info.item_number, info.realVuid);
            await axios.put(`${baseURL}/api/project-contract-items/${info.realVuid}/allocations`, {
              allocations: [{ cost_code_vuid: info.cost_code_vuid, cost_type_vuid: info.cost_type_vuid }]
            });
          }
        } catch (postErr) {
          console.error('Post-pass allocation error for item:', info.item_number, info.realVuid, postErr);
        }
      }
      
      // Update contract total amount
      if (selectedContract?.vuid) {
        const updatedItems = contractItems.filter(item => !item.vuid.startsWith('temp-'));
        // Round to 2 decimal places to avoid floating point precision issues
        const total = Math.round(updatedItems.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0) * 100) / 100;
        
        try {
          await axios.put(`${baseURL}/api/project-contracts/${selectedContract.vuid}`, {
            contract_amount: total
          });
          // Update local contract state
          setContracts(prev => prev.map(c => 
            c.vuid === selectedContract.vuid ? { ...c, contract_amount: total } : c
          ));
          setSelectedContract(prev => prev ? { ...prev, contract_amount: total } : null);
        } catch (err) {
          console.error('Error updating contract total:', err);
        }
      }
      
      setError('');
      setSuccessMessage('All contract items saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Error saving contract items: ' + err.message);
    }
  };

  // Handle Done button - close lines view and refresh contracts
  const handleDoneEditing = async () => {
    try {
      // Close the lines view
      setShowItemsForm(false);
      
      // Refresh the project contracts data
      await fetchData();
      
      // Show success message
      setSuccessMessage('Contract lines closed and data refreshed!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error refreshing contracts:', err);
      setError('Error refreshing contracts: ' + err.message);
    }
  };

  // Option: auto-create contract items from budget lines when creating a contract
  const [createItemsFromBudget, setCreateItemsFromBudget] = useState(true);
  const [budgetIncreasePercentage, setBudgetIncreasePercentage] = useState(0);
  
  // Post-creation percentage adjustment
  const [adjustmentPercentage, setAdjustmentPercentage] = useState(0);
  const [showPercentageAdjustment, setShowPercentageAdjustment] = useState(false);
  const [originalAmounts, setOriginalAmounts] = useState({}); // Store original amounts before adjustments

  // Function to apply percentage adjustment to all contract items
  const applyPercentageAdjustment = async () => {
    if (!selectedContract || !contractItems.length) return;
    
    try {
      setError('');
      
      // Store original amounts if not already stored
      if (Object.keys(originalAmounts).length === 0) {
        const originals = {};
        contractItems.forEach(item => {
          originals[item.vuid] = item.total_amount;
        });
        setOriginalAmounts(originals);
      }
      
      // Calculate new amounts and update each item
      const updatePromises = contractItems.map(async (item) => {
        const originalAmount = originalAmounts[item.vuid] || item.total_amount;
        const adjustmentMultiplier = 1 (adjustmentPercentage / 100);
        const newAmount = originalAmount * adjustmentMultiplier;
        
        // Update the item via API
        const response = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
          total_amount: newAmount,
          unit_price: newAmount // Assuming quantity is + 1 for budget-based items
        });
        
        return response.data;
      });
      
      await Promise.all(updatePromises);
      
      // Refresh contract items to show updated amounts
      await fetchContractItems(selectedContract.vuid);
      
      setSuccessMessage(`Successfully applied ${adjustmentPercentage}% adjustment to all contract items`);
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error applying percentage adjustment:', error);
      setError('Error applying percentage adjustment: ' + error.message);
    }
  };
  
  // Function to reset amounts to original values
  const resetToOriginalAmounts = async () => {
    if (!selectedContract || !contractItems.length || Object.keys(originalAmounts).length === 0) return;
    
    try {
      setError('');
      
      // Reset each item to its original amount
      const updatePromises = contractItems.map(async (item) => {
        const originalAmount = originalAmounts[item.vuid];
        if (originalAmount !== undefined) {
          const response = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
            total_amount: originalAmount,
            unit_price: originalAmount
          });
          return response.data;
        }
      });
      
      await Promise.all(updatePromises);
      
      // Refresh contract items and reset states
      await fetchContractItems(selectedContract.vuid);
      setAdjustmentPercentage(0);
      setOriginalAmounts({});
      
      setSuccessMessage('Successfully reset all amounts to original values');
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error resetting to original amounts:', error);
      setError('Error resetting amounts: ' + error.message);
    }
  };

  // Helper: create contract items for each budget line in the selected project
  const generateItemsFromBudget = async (newContract) => {
    try {
      const projectVuid = formData.project_vuid || newContract.project_vuid;
      if (!projectVuid) return;

      // Get all budgets for the project
      const budgetsRes = await axios.get(`${baseURL}/api/project-budgets`);
      const projectBudgets = (budgetsRes.data || []).filter(b => b.project_vuid === projectVuid);

      // Gather all budget lines across budgets
      let allLines = [];
      for (const budget of projectBudgets) {
        try {
          const linesRes = await axios.get(`${baseURL}/api/project-budgets/${budget.vuid}/lines`);
          const lines = linesRes.data || [];
          allLines = allLines.concat(lines);
        } catch (e) {
          console.warn('Failed to fetch lines for budget', budget.vuid, e);
        }
      }

      // Get project-specific cost codes for this project
      let projectCostCodes = [];
      try {
        const projectCostCodesRes = await axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`);
        projectCostCodes = projectCostCodesRes.data || [];
        console.log(`Fetched ${projectCostCodes.length} project cost codes`);
      } catch (e) {
        console.warn('Failed to fetch project cost codes:', e);
      }

      // Create a contract item per budget line
      let index = 1;
      const totalLines = allLines.length;
      console.log(`🔄 Processing ${totalLines} budget lines to create contract items`);
      setBudgetLinesProgress({ current: 0, total: totalLines });
      const createdItems = []; // Track created items to add to state immediately
      
      for (const line of allLines) {
        setBudgetLinesProgress({ current: index, total: totalLines });
        const item_number = String(index).padStart(4, '0');
        const cost_code_vuid = line.cost_code_vuid || null;
        const cost_type_vuid = line.cost_type_vuid || null;
        
        // Look for cost code in both global and project-specific cost codes
        let costCode = costCodes.find(c => c.vuid === cost_code_vuid);
        if (!costCode) {
          costCode = projectCostCodes.find(c => c.vuid === cost_code_vuid);
        }
        
        // Use the budget line description as-is, don't add cost code to description
        const description = line.description || 'Budget Line';
        const budgetAmount = parseFloat(line.budget_amount || 0);
        
        // Apply percentage increase if specified
        const increaseMultiplier = 1 + (budgetIncreasePercentage / 100);
        // Round to 2 decimal places to avoid floating point precision issues
        const total_amount = Math.round((budgetAmount * increaseMultiplier) * 100) / 100;
        
        console.log(`Line ${index}: cost_code_vuid=${cost_code_vuid}, cost_type_vuid=${cost_type_vuid}, budget_amount=${budgetAmount}, markup=${budgetIncreasePercentage}%, total_amount=${total_amount}`);

        try {
          console.log(`Creating contract item ${item_number} for contract ${newContract.vuid}`);
          const itemRes = await axios.post(`${baseURL}/api/project-contract-items`, {
            contract_vuid: newContract.vuid,
            item_number,
            cost_code_vuid,
            cost_type_vuid,
            description,
            quantity: 1, // Default quantity for budget-based items
            unit_price: total_amount, // Use budget amount as unit price
            total_amount,
            markup_percentage: budgetIncreasePercentage,
            status: 'active',
            notes: ''
          });
          const savedItem = itemRes.data;
          console.log(`✅ Contract item created successfully:`, savedItem);
          createdItems.push(savedItem); // Add to array for immediate state update

          // Create a matching allocation if both are present
          if (cost_code_vuid && cost_type_vuid) {
            console.log(`🔄 Creating allocation for item ${savedItem.vuid} with cost_code: ${cost_code_vuid}, cost_type: ${cost_type_vuid}`);
            try {
              const allocationRes = await axios.post(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`, {
                cost_code_vuid,
                cost_type_vuid,
                notes: 'Auto-generated from budget line'
              });
              console.log('✅ Allocation created successfully:', allocationRes.data);
              
              // Update local allocations cache for immediate display
              setAllocationsByItem(prev => ({
                ...prev,
                [savedItem.vuid]: [allocationRes.data.allocation || { cost_code_vuid, cost_type_vuid }]
              }));
            } catch (allocationError) {
              console.error('❌ Failed to create allocation:', allocationError);
              console.error('❌ Allocation error response:', allocationError.response?.data);
              console.error('❌ Allocation error status:', allocationError.response?.status);
            }
          } else {
            console.log(`⚠️ Skipping allocation for item ${savedItem.vuid} - missing cost_code_vuid or cost_type_vuid (cost_code: ${cost_code_vuid}, cost_type: ${cost_type_vuid})`);
          }
        } catch (e) {
          console.error('❌ Failed creating contract item from budget line', line?.vuid, e);
        }

        index += 1;
      }
      
      // Reset progress when complete
      setBudgetLinesProgress({ current: 0, total: 0 });
      
      // Immediately add created items to state instead of refetching
      if (createdItems.length > 0) {
        console.log(`✅ Adding ${createdItems.length} created items to state immediately`);
        
        // Update contract items state
        setContractItems(prev => {
          // Merge with existing items, avoiding duplicates
          const existingVuids = new Set(prev.map(item => item.vuid));
          const newItems = createdItems.filter(item => !existingVuids.has(item.vuid));
          return [...prev, ...newItems].sort((a, b) => {
            // Sort by item_number
            return (a.item_number || '').localeCompare(b.item_number || '');
          });
        });
        
        // Calculate total from all created items (including any existing items)
        // Do this after state update using a callback
        setTimeout(() => {
          setContractItems(currentItems => {
            // Calculate total from all items
            const total = Math.round(currentItems.reduce((sum, item) => {
              const amount = parseFloat(item.total_amount || 0) || 0;
              console.log(`  Item ${item.item_number}: $${amount.toFixed(2)}`);
              return sum + amount;
            }, 0) * 100) / 100;
            console.log(`💰 Calculated contract total: $${total.toFixed(2)} from ${currentItems.length} items`);
            
            // Update local contract state
            setContracts(prev => prev.map(c => 
              c.vuid === newContract.vuid ? { ...c, contract_amount: total } : c
            ));
            
            // Update selected contract if it's the current one
            setSelectedContract(prev => {
              if (prev && prev.vuid === newContract.vuid) {
                return { ...prev, contract_amount: total };
              }
              return prev;
            });
            
            // Update the contract amount in the backend (non-blocking)
            axios.put(`${baseURL}/api/project-contracts/${newContract.vuid}`, {
              contract_amount: total
            }).then(() => {
              console.log(`✅ Contract amount updated to $${total.toFixed(2)} for contract ${newContract.vuid}`);
            }).catch(err => {
              console.warn('⚠️ Error updating contract amount:', err);
            });
            
            return currentItems; // Return unchanged
          });
        }, 200);
        
        console.log(`✅ State updated with ${createdItems.length} new items`);
      }
    } catch (err) {
      console.error('❌ Error generating items from budget:', err);
      // Reset progress on error
      setBudgetLinesProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center text-red-600">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Project Contracts</h1>
          <p className="text-xl text-gray-600">Manage project contracts and sales orders</p>
        </div>

        {/* Unallocated Budget Lines Alert */}
        {showAllocationAlert && allocationStatus.has_unallocated_budget_lines && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75-1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Unallocated Budget Lines
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    {allocationStatus.budget_lines_without_allocations} of {allocationStatus.total_budget_lines} budget lines 
                    are not allocated to any contract items. This may indicate missing contract coverage or budget planning issues.
                  </p>
                  {allocationStatus.unallocated_budget_lines && allocationStatus.unallocated_budget_lines.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium">Budget lines needing contract allocations:</p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        {allocationStatus.unallocated_budget_lines.slice(0, 5).map((budgetLine, index) => (
                          <li key={index} className="text-xs">
                            {budgetLine.cost_code?.code || 'Unknown'} - {budgetLine.cost_type?.abbreviation || 'Unknown'}: {budgetLine.notes || 'No description'} 
                            ({formatCurrency(budgetLine.budget_amount)})
                          </li>
                        ))}
                        {allocationStatus.unallocated_budget_lines.length > 5 && (
                          <li className="text-xs text-amber-600">
                            ... and {allocationStatus.unallocated_budget_lines.length - 5} more budget lines
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mb-8 flex justify-center space-x-3">
          <button
            onClick={handleShowCreateForm}
            className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
            data-tooltip="Add Contract"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Contract</span>
          </button>
          <button
            onClick={() => {
              const searchParams = new URLSearchParams(location.search);
              const projectVuid = searchParams.get('project');
              const url = projectVuid ? `/external-change-orders?project=${projectVuid}` : '/external-change-orders';
              navigate(url);
            }}
            className="flex items-center space-x-2 bg-white hover:bg-gray-50 font-medium py-2 px-4 rounded text-base text-gray-900 border border-gray-300 transition-colors"
            data-tooltip="External Change Orders"
          >
            <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>External Change Orders</span>
          </button>
        </div>

        {/* Create/Edit Contract Form */}
        {showCreateForm && (
          <div ref={contractFormHeaderRef} className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
              {editingContract ? 'Edit Contract' : 'Add New Contract'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-medium">{errors.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="project_vuid" className="block text-lg font-semibold text-gray-900 mb-2">
                    Project *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder={formData.project_vuid ? "Type to search projects..." : "Select project..."}
                      value={formData.project_vuid ? 
                        (projects.find(p => p.vuid === formData.project_vuid)?.project_name || '') : 
                        projectSearchTerm
                      }
                      onChange={(e) => {
                        if (!formData.project_vuid) {
                          setProjectSearchTerm(e.target.value);
                          setProjectDropdownOpen(true);
                        }
                      }}
                      onClick={() => {
                        if (!formData.project_vuid) {
                          setProjectDropdownOpen(!projectDropdownOpen);
                        } else {
                          // Clear selection to allow searching
                          setFormData(prev => ({ ...prev, project_vuid: '' }));
                          setProjectSearchTerm('');
                          setProjectDropdownOpen(true);
                        }
                      }}
                      className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.project_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {projectDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            placeholder="Search projects..."
                            value={projectSearchTerm}
                            onChange={(e) => setProjectSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                        {getFilteredProjects().length > 0 ? (
                          getFilteredProjects().map(project => (
                            <div
                              key={project.vuid}
                              onClick={async () => {
                                setFormData(prev => ({ ...prev, project_vuid: project.vuid }));
                                setProjectDropdownOpen(false);
                                setProjectSearchTerm('');
                              
                              // Fetch project-specific cost codes for this project
                              try {
                                const projectCostCodesRes = await axios.get(`${baseURL}/api/projects/${project.vuid}/cost-codes`);
                                setProjectCostCodes(projectCostCodesRes.data || []);
                                console.log(`Fetched ${projectCostCodesRes.data?.length || 0} project cost codes for project ${project.project_name}`);
                              } catch (e) {
                                console.warn('Failed to fetch project cost codes:', e);
                                setProjectCostCodes([]);
                              }
                              
                              // Fetch project budget lines for this project
                              try {
                                const budgetLinesRes = await axios.get(`${baseURL}/api/projects/${project.vuid}/budget-lines`);
                                setProjectBudgetLines(budgetLinesRes.data || []);
                                console.log(`Fetched ${budgetLinesRes.data?.length || 0} budget lines for project ${project.project_name}`);
                              } catch (e) {
                                console.warn('Failed to fetch project budget lines:', e);
                                setProjectBudgetLines([]);
                              }
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{project.project_number}</div>
                            <div className="text-sm text-gray-600">{project.project_name}</div>
                          </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 text-center">
                            No projects found matching "{projectSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.project_vuid && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_vuid}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contract_number" className="block text-lg font-semibold text-gray-900 mb-2">
                    Contract Number *
                  </label>
                  <input
                    type="text"
                    id="contract_number"
                    name="contract_number"
                    value={formData.contract_number}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.contract_number ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter contract number"
                    required
                  />
                  {errors.contract_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.contract_number}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="customer_vuid" className="block text-lg font-semibold text-gray-900 mb-2">
                    Customer *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder={formData.customer_vuid ? "Type to search customers..." : "Select customer..."}
                      value={formData.customer_vuid ? 
                        (customers.find(c => c.vuid === formData.customer_vuid)?.customer_name || '') : 
                        customerSearchTerm
                      }
                      onChange={(e) => {
                        if (!formData.customer_vuid) {
                          setCustomerSearchTerm(e.target.value);
                          setCustomerDropdownOpen(true);
                        }
                      }}
                      onClick={() => {
                        if (!formData.customer_vuid) {
                          setCustomerDropdownOpen(!customerDropdownOpen);
                        } else {
                          // Clear selection to allow searching
                          setFormData(prev => ({ ...prev, customer_vuid: '' }));
                          setCustomerSearchTerm('');
                          setCustomerDropdownOpen(true);
                        }
                      }}
                      className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.customer_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {customerDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                        {getFilteredCustomers().length > 0 ? (
                          getFilteredCustomers().map(customer => (
                            <div
                              key={customer.vuid}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, customer_vuid: customer.vuid }));
                                setCustomerDropdownOpen(false);
                                setCustomerSearchTerm('');
                              }}
                              className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{customer.customer_name}</div>
                              <div className="text-sm text-gray-600">{customer.company_name}</div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 text-center">
                            No customers found matching "{customerSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                    {errors.customer_vuid && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_vuid}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="accounting_period_vuid" className="block text-lg font-semibold text-gray-900 mb-2">
                    Accounting Period *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select accounting period..."
                      value={accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid) ? 
                        `${accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid)?.month}/${accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid)?.year}` : ''}
                      onClick={() => setAccountingPeriodDropdownOpen(!accountingPeriodDropdownOpen)}
                      readOnly
                      className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.accounting_period_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {accountingPeriodDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {accountingPeriods.map(period => (
                          <div
                            key={period.vuid}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, accounting_period_vuid: period.vuid }));
                              setAccountingPeriodDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{period.month}/{period.year}</div>
                            <div className="text-sm text-gray-600">{period.status === 'open' ? 'Open' : 'Closed'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.accounting_period_vuid && (
                      <p className="mt-1 text-sm text-red-600">{errors.accounting_period_vuid}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="contract_type" className="block text-lg font-semibold text-gray-900 mb-2">
                    Contract Type *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select contract type..."
                      value={
                        CONTRACT_TYPES.find(type => type.value === formData.contract_type)?.label || ''
                      }
                      onClick={() => setContractTypeDropdownOpen(!contractTypeDropdownOpen)}
                      readOnly
                      className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.contract_type ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {contractTypeDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          {CONTRACT_TYPES.map((type, index) => (
                            <div
                              key={type.value}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, contract_type: type.value }));
                                setContractTypeDropdownOpen(false);
                              }}
                              className={`px-4 py-3 hover:bg-gray-100 cursor-pointer ${
                                index !== CONTRACT_TYPES.length - 1 ? 'border-b border-gray-100' : ''
                              }`}
                            >
                              {type.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.contract_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.contract_type}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="status" className="block text-lg font-semibold text-gray-900 mb-2">
                    Status
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select status..."
                      value={formData.status === 'active' ? 'Active' : 
                             formData.status === 'completed' ? 'Completed' : 
                             formData.status === 'on-hold' ? 'On Hold' : 
                             formData.status === 'cancelled' ? 'Cancelled' : 
                             formData.status}
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      readOnly
                      className="w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all border-gray-300"
                    />
                    {statusDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'active' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            Active
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'completed' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            Completed
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'on-hold' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            On Hold
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'cancelled' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer"
                          >
                            Cancelled
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-lg font-semibold text-gray-900 mb-2">
                    Description *
                  </label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter contract description"
                    required
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>

                {/* Auto-calculated Amount (read-only) */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-2">
                    Total Amount (auto)
                  </label>
                  <div className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-lg font-bold bg-gray-50">
                    ${Array.isArray(contractItems) && contractItems.length > 0
                      ? contractItems.reduce((sum, it) => sum + (parseFloat(it.total_amount || 0) || 0), 0).toFixed(2)
                      : '0.00'}
                  </div>
                </div>

                <div>
                  <label htmlFor="start_date" className="block text-lg font-semibold text-gray-900 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.start_date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.start_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="end_date" className="block text-lg font-semibold text-gray-900 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 border-2 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.end_date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="notes" className="block text-lg font-semibold text-gray-900 mb-2">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all resize-none"
                    placeholder="Enter additional notes"
                  />
                </div>
              </div>

              {/* Option: Create items from budget lines - show if project is selected */}
              {formData.project_vuid && (
                <div className="mt-2">
                  <label className="inline-flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={createItemsFromBudget}
                      onChange={(e) => setCreateItemsFromBudget(e.target.checked)}
                      className="h-5 w-5 text-gray-800 border-gray-300 rounded focus:ring-gray-800"
                    />
                    <span className="text-gray-900 text-md font-medium">
                      Create Contract Items based on Budget lines
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    When selected, a contract item will be created for each project budget line with matching cost code and cost type, amount prefilled, and line numbers auto-incremented starting at 0001.
                    {projectBudgetLines.length > 0 ? (
                      <span className="block mt-1 text-green-600 font-medium">
                        ✓ {projectBudgetLines.length} budget lines available for this project
                      </span>
                    ) : (
                      <span className="block mt-1 text-orange-600 font-medium">
                        ⚠ No budget lines found for this project - items will need to be created manually
                      </span>
                    )}
                    {projectSettings.allow_project_cost_codes === 'true' && (
                      <span className="block mt-1 text-blue-600 font-medium">
                        Project-specific cost codes are enabled - any cost code format will be supported.
                      </span>
                    )}
                  </p>
                  
                  {createItemsFromBudget && projectBudgetLines.length > 0 && (
                    <div className="mt-3 ml-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Budget Increase Percentage (Optional)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={budgetIncreasePercentage}
                          onChange={(e) => setBudgetIncreasePercentage(parseFloat(e.target.value) || 0)}
                          min="0"
                          max="200"
                          step="0.1"
                          placeholder="0.0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-gray-600">%</span>
                        <span className="text-sm text-gray-500">
                          (e.g., 15% for contractor fee)
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Contract amounts will be: Budget Amount × (1 {budgetIncreasePercentage}%)
                        {budgetIncreasePercentage > 0 && (
                          <span className="text-blue-600 font-medium">
                            {' '}= Budget Amount × {(1 + budgetIncreasePercentage / 100).toFixed(3)}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-center space-x-4">
                <button
                  type="submit"
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  {editingContract ? 'Update Contract' : 'Create Contract'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg text-lg transition-all duration-200 transform hover:scale-105 shadow-lg"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Allocations Modal */}
        {console.log('🔧 Modal render check:', { showAllocationsModal, selectedItemForAllocations: !!selectedItemForAllocations })}
        {showAllocationsModal && selectedItemForAllocations && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Manage Allocations for Item: {selectedItemForAllocations.item_number}
                </h2>
                <button
                  onClick={() => setShowAllocationsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Allocate this contract item across multiple cost codes and cost types.
                </p>

                {/* Current Allocations */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Allocations</h3>
                  {allocations.length === 0 ? (
                    <p className="text-gray-500 italic">No allocations defined yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {allocations.map((allocation) => (
                        <div key={allocation.vuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {getAllCostCodes().find(c => c.vuid === allocation.cost_code_vuid)?.code} - 
                              {costTypes.find(t => t.vuid === allocation.cost_type_vuid)?.cost_type}
                            </div>
                            <div className="text-sm text-gray-600">
                              {getAllCostCodes().find(c => c.vuid === allocation.cost_code_vuid)?.description} - 
                              {costTypes.find(t => t.vuid === allocation.cost_type_vuid)?.description}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleDeleteAllocation(allocation.vuid)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Allocation */}
                {!showAddAllocationForm ? (
                  <button
                    onClick={() => setShowAddAllocationForm(true)}
                    className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-colors"
                  >
                    Add Allocation
                  </button>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Only unallocated cost code/type combinations from the project budget and actual costs (AP invoices, labor costs, project expenses) are shown. This prevents duplicate allocations across all contract items in this project.
                      </p>
                    </div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Add New Allocation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cost Code</label>
                        <select
                          value={newAllocation.cost_code_vuid}
                          onChange={(e) => setNewAllocation({...newAllocation, cost_code_vuid: e.target.value, cost_type_vuid: ''})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Cost Code</option>
                          {(() => {
                            // Get all allocated cost code/type combinations for this project
                            const contract = contracts.find(c => c.vuid === selectedItemForAllocations?.contract_vuid);
                            const allProjectAllocations = contract?.items ? 
                              contract.items.flatMap(item => allocationsByItem[item.vuid] || []).map(alloc => 
                                `${alloc.cost_code_vuid}_${alloc.cost_type_vuid}`
                              ) : [];
                            
                            // Get unallocated combinations from project budget
                            const unallocatedCombinationsFromBudget = projectBudgetLines.length === 0 ? [] :
                              projectBudgetLines.filter(budgetLine => 
                                budgetLine.cost_code_vuid && budgetLine.cost_type_vuid &&
                                !allProjectAllocations.includes(`${budgetLine.cost_code_vuid}_${budgetLine.cost_type_vuid}`)
                              );
                            
                            // Get unallocated combinations from actual costs (AP invoices, labor, expenses)
                            const unallocatedCombinationsFromCosts = unallocatedCosts.filter(cost =>
                              cost.cost_code_vuid && cost.cost_type_vuid &&
                              !allProjectAllocations.includes(`${cost.cost_code_vuid}_${cost.cost_type_vuid}`)
                            );
                            
                            // Combine both sources
                            const allUnallocatedCombinations = [
                              ...unallocatedCombinationsFromBudget,
                              ...unallocatedCombinationsFromCosts
                            ];
                            
                            // Get unique cost codes from all unallocated combinations
                            const unallocatedCostCodeVuids = new Set(
                              allUnallocatedCombinations.map(item => item.cost_code_vuid).filter(Boolean)
                            );
                            
                            return getAllCostCodes()
                              .filter(isCostCodeSelectable)
                              .filter(costCode => 
                                projectBudgetLines.length === 0 && unallocatedCosts.length === 0 ||
                                unallocatedCostCodeVuids.has(costCode.vuid)
                              )
                              .map((costCode) => (
                                <option key={costCode.vuid} value={costCode.vuid}>
                                  {costCode.code} - {costCode.description}
                                </option>
                              ));
                          })()}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cost Type</label>
                        <select
                          value={newAllocation.cost_type_vuid}
                          onChange={(e) => setNewAllocation({...newAllocation, cost_type_vuid: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!newAllocation.cost_code_vuid}
                        >
                          <option value="">{newAllocation.cost_code_vuid ? 'Select Cost Type' : 'Select Cost Code First'}</option>
                          {(() => {
                            if (!newAllocation.cost_code_vuid) return [];
                            
                            // Get all allocated cost code/type combinations for this project
                            const contract = contracts.find(c => c.vuid === selectedItemForAllocations?.contract_vuid);
                            const allProjectAllocations = contract?.items ? 
                              contract.items.flatMap(item => allocationsByItem[item.vuid] || []).map(alloc => 
                                `${alloc.cost_code_vuid}_${alloc.cost_type_vuid}`
                              ) : [];
                            
                            // Get unallocated combinations from project budget for the selected cost code
                            const unallocatedCombinationsFromBudget = projectBudgetLines.length === 0 ? [] :
                              projectBudgetLines.filter(budgetLine => 
                                budgetLine.cost_code_vuid === newAllocation.cost_code_vuid &&
                                budgetLine.cost_type_vuid &&
                                !allProjectAllocations.includes(`${budgetLine.cost_code_vuid}_${budgetLine.cost_type_vuid}`)
                              );
                            
                            // Get unallocated combinations from actual costs for the selected cost code
                            const unallocatedCombinationsFromCosts = unallocatedCosts.filter(cost =>
                              cost.cost_code_vuid === newAllocation.cost_code_vuid &&
                              cost.cost_type_vuid &&
                              !allProjectAllocations.includes(`${cost.cost_code_vuid}_${cost.cost_type_vuid}`)
                            );
                            
                            // Combine cost types from both sources
                            const unallocatedCostTypeVuidsFromBudget = new Set(
                              unallocatedCombinationsFromBudget.map(bl => bl.cost_type_vuid).filter(Boolean)
                            );
                            const unallocatedCostTypeVuidsFromCosts = new Set(
                              unallocatedCombinationsFromCosts.map(cost => cost.cost_type_vuid).filter(Boolean)
                            );
                            
                            // Combine both sets
                            const allUnallocatedCostTypeVuids = new Set([
                              ...unallocatedCostTypeVuidsFromBudget,
                              ...unallocatedCostTypeVuidsFromCosts
                            ]);
                            
                            // Show cost types that are either:
                            // 1. In unallocated budget combinations, OR
                            // 2. In unallocated actual costs, OR
                            // 3. If no budget/costs, show all active types
                            return costTypes
                              .filter(isCostTypeSelectable)
                              .filter(costType => {
                                // Check if this combination is already allocated
                                const combinationKey = `${newAllocation.cost_code_vuid}_${costType.vuid}`;
                                const isAlreadyAllocated = allProjectAllocations.includes(combinationKey);
                                if (isAlreadyAllocated) return false;
                                
                                // If no constraints, show all
                                if (projectBudgetLines.length === 0 && unallocatedCosts.length === 0) {
                                  return true;
                                }
                                
                                // Show if in unallocated combinations (from budget or costs)
                                return allUnallocatedCostTypeVuids.has(costType.vuid);
                              })
                              .map((costType) => (
                                <option key={costType.vuid} value={costType.vuid}>
                                  {costType.cost_type} ({costType.abbreviation})
                                </option>
                              ));
                          })()}
                        </select>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddAllocation}
                        className="bg-gray-50 hover:bg-gray-100 text-gray-900 font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Add Allocation
                      </button>
                      <button
                        onClick={() => setShowAddAllocationForm(false)}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAllocationsModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAllocations}
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Save Allocations
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search contracts..."
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Project Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
              >
                Project: {projectFilter === 'all' ? 'All' : projects.find(p => p.vuid === projectFilter)?.project_name || 'Unknown'}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {projectDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setProjectFilter('all');
                        setProjectDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All Projects
                    </button>
                    {projects.map(project => (
                      <button
                        key={project.vuid}
                        onClick={() => {
                          setProjectFilter(project.vuid);
                          setProjectDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        {project.project_number} - {project.project_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                className="px-6 py-3 border-2 border-gray-300 rounded-lg text-lg font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent transition-all"
              >
                Status: {statusFilter === 'all' ? 'All' : statusFilter}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('active');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Active
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('completed');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('cancelled');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Cancelled
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('on_hold');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-3 text-lg font-medium text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      On Hold
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Contracts List */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Contracts</h2>
          
          {/* Contracts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginatedContracts.map((contract) => (
              <div key={contract.vuid} className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-200">
                <div className="flex items-center space-x-4 mb-3">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{contract.contract_number}</h3>
                    <p className="text-lg text-gray-600 font-medium">{contract.description}</p>
                    <div className="flex items-center space-x-4 mt-1">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        contract.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                      </span>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 font-mono">
                        {CONTRACT_TYPES.find(type => type.value === contract.contract_type)?.label || 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Project:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {projects.find(p => p.vuid === contract.project_vuid)?.project_name || 'Unknown'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Customer:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {customers.find(c => c.vuid === contract.customer_vuid)?.customer_name || 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Accounting Period:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {accountingPeriods.find(ap => ap.vuid === contract.accounting_period_vuid) ? 
                        `${accountingPeriods.find(ap => ap.vuid === contract.accounting_period_vuid)?.month}/${accountingPeriods.find(ap => ap.vuid === contract.accounting_period_vuid)?.year}` : 'Not specified'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Amount:</span>
                    <span className="text-sm font-bold text-gray-900">
                      ${parseFloat(contract.contract_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Start Date:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {contract.start_date ? new Date(contract.start_date).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">End Date:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {contract.end_date ? new Date(contract.end_date).toLocaleDateString() : 'Not set'}
                    </span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <button
                    onClick={() => selectedContract && selectedContract.vuid === contract.vuid && showItemsForm ? handleHideItems() : handleViewItems(contract)}
                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                    data-tooltip={selectedContract && selectedContract.vuid === contract.vuid && showItemsForm ? 'Hide Items' : 'View Items'}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedContract(contract);
                      setShowFeeSettings(true);
                    }}
                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                    data-tooltip="Manage fee settings"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleIntegrationModal(contract)}
                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                    data-tooltip="Send to integration"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleEditContract(contract)}
                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                    data-tooltip="Edit contract"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleDeleteContract(contract)}
                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                    data-tooltip="Delete contract"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8 flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="px-4 py-2 text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
          
          {paginatedContracts.length === 0 && (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-lg font-medium text-gray-900 mb-2">No contracts found</p>
              <p className="text-gray-600">Get started by creating your first contract.</p>
            </div>
          )}

          {/* Contract Items Form - MOVED HERE */}
          {showItemsForm && selectedContract && (
            <div className="bg-white rounded-xl shadow-2xl p-8 mb-12 border border-gray-200">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                Contract Items Entry - Excel Style
              </h2>
              
              {/* Success Message */}
              {successMessage && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-600 font-medium">{successMessage}</p>
                </div>
              )}
              
              {/* Percentage Adjustment Controls */}
              {contractItems.length > 0 && (
                <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Mark-up Adjustments</h3>
                    <button
                      onClick={() => setShowPercentageAdjustment(!showPercentageAdjustment)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {showPercentageAdjustment ? 'Hide' : 'Show'} Adjustment Tools
                    </button>
                  </div>
                  
                  {showPercentageAdjustment && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium text-gray-700">
                          Mark-up Percentage:
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={adjustmentPercentage}
                            onChange={(e) => setAdjustmentPercentage(parseFloat(e.target.value) || 0)}
                            min="-50"
                            max="200"
                            step="0.1"
                            placeholder="0.0"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-gray-600">%</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={applyPercentageAdjustment}
                            disabled={adjustmentPercentage === 0}
                            className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-900 rounded-md disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                          >
                            Apply to All Items
                          </button>
                          {Object.keys(originalAmounts).length > 0 && (
                            <button
                              onClick={resetToOriginalAmounts}
                              className="px-4 py-2 bg-gray-50 hover:bg-gray-100 text-gray-900 rounded-md text-sm font-medium"
                            >
                              Reset to Original
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <p>
                          <strong>Current calculation:</strong> Original Amount × (1 {adjustmentPercentage}%)
                          {adjustmentPercentage !== 0 && (
                            <span className="text-blue-600 font-medium">
                              {' '}= Original Amount × {(1 + adjustmentPercentage / 100).toFixed(3)}
                            </span>
                          )}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          This will update all contract item amounts. Use positive percentages for markups (e.g., 15%) or negative for discounts (e.g., -5%).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="mb-6">
                <p className="text-gray-600 text-center">
                  Use Tab to navigate between fields, Enter to add a new line, and start typing in dropdowns to search
                </p>
              </div>

              {/* Add Item Button */}
              <div className="mb-4 text-center">
                <button
                  type="button"
                  onClick={handleAddNewItem}
                  className="bg-gray-50 hover:bg-gray-100 font-semibold py-2 px-4 rounded-lg transition-all duration-200 hover:scale-105"
                  data-tooltip="Add new contract item"
                >
                  Add Item
                </button>
              </div>

              {/* Loading indicator for budget lines processing */}
              {budgetLinesProgress.total > 0 && budgetLinesProgress.current < budgetLinesProgress.total && (
                <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-blue-900">
                        Creating contract items from budget lines...
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        Processing {budgetLinesProgress.current} of {budgetLinesProgress.total} budget lines
                      </p>
                      <div className="mt-2 w-full bg-blue-200 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${(budgetLinesProgress.current / budgetLinesProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Excel-style grid */}
              <div className="overflow-x-auto">
                <table key={`contract-items-${projectCostCodes.length}`} className="min-w-max border border-gray-300" style={{ position: 'relative' }}>
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Item # *</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Cost Code</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Cost Type</th>
                      {feeLevel === 'contract_item' && (
                        <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Markup %</th>
                      )}
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Description *</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Total Amount *</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Change Order Amount</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Total with Changes</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Notes</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {console.log('🎨 Rendering contractItems:', contractItems.length, 'items', contractItems)}
                    {contractItems && contractItems.length > 0 ? (
                      contractItems
                        .sort((a, b) => {
                          const aNum = parseInt(a.item_number || '0', 10);
                          const bNum = parseInt(b.item_number || '0', 10);
                          return aNum - bNum;
                        })
                        .map((item, index) => (
                          <tr key={item.vuid || index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-2">
                              <input
                                type="text"
                                value={item.item_number || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], item_number: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                                placeholder="Item #"
                              />
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {projectSettings?.allow_project_cost_codes ? (
                                // Project-specific mode: text input
                                <input
                                  type="text"
                                  value={getCostCodeInputValue(item)}
                                  onChange={(e) => {
                                    const newCostCodeValue = e.target.value;
                                    const newItems = [...contractItems];
                                    newItems[index] = { ...newItems[index], cost_code_vuid: newCostCodeValue };
                                    setContractItems(newItems);
                                  }}
                                  className="w-full px-2 py-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                                  placeholder="Enter cost code..."
                                />
                              ) : (
                                // Global mode: dropdown
                                <select
                                  value={item.cost_code_vuid || ''}
                                  onChange={async (e) => {
                                    const newCostCodeVuid = e.target.value;
                                    const oldMarkup = feeLevel === 'contract_item' 
                                      ? getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid) ?? (item.markup_percentage ?? 0)
                                      : (item.markup_percentage ?? 0);
                                    const newItems = [...contractItems];
                                    newItems[index] = { ...newItems[index], cost_code_vuid: newCostCodeVuid };
                                    setContractItems(newItems);
                                  
                                  // Auto-select cost type if there's only one option for this cost code
                                  if (newCostCodeVuid) {
                                    const availableCostTypes = costTypes
                                      .filter(isCostTypeSelectable)
                                      .filter(costType => 
                                        projectBudgetLines.some(budgetLine => 
                                          budgetLine.cost_type_vuid === costType.vuid &&
                                          budgetLine.cost_code_vuid === newCostCodeVuid
                                        )
                                      );
                                    
                                    console.log('Grid: Available cost types for cost code:', newCostCodeVuid, ':', availableCostTypes);
                                    
                                    if (availableCostTypes.length === 1) {
                                      console.log('Grid: Auto-selecting single cost type:', availableCostTypes[0].cost_type);
                                      const newCostTypeVuid = availableCostTypes[0].vuid;
                                      newItems[index] = { ...newItems[index], cost_type_vuid: newCostTypeVuid };
                                      
                                      // Update markup and recalculate total_amount if fee level is contract_item
                                      if (feeLevel === 'contract_item') {
                                        const newMarkup = getMarkupFromFeeSettings(newCostCodeVuid, newCostTypeVuid) ?? 0;
                                        const currentTotal = parseFloat(newItems[index].total_amount || 0);
                                        const recalculatedTotal = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
                                        newItems[index] = { 
                                          ...newItems[index], 
                                          markup_percentage: newMarkup,
                                          total_amount: recalculatedTotal
                                        };
                                      }
                                      
                                      setContractItems(newItems);
                                      
                                      // Handle allocation update with auto-selected cost type
                                      await handleCostCodeTypeChange(item.vuid, newCostCodeVuid, newCostTypeVuid);
                                    } else if (availableCostTypes.length > 1) {
                                      console.log('Grid: Multiple cost types available, user must select');
                                      // Clear cost type so user can choose
                                      newItems[index] = { ...newItems[index], cost_type_vuid: '' };
                                      setContractItems(newItems);
                                      
                                      // Handle allocation update (cost type will be cleared)
                                      await handleCostCodeTypeChange(item.vuid, newCostCodeVuid, '');
                                    } else {
                                      console.log('Grid: No cost types available for this cost code');
                                      // Clear cost type
                                      newItems[index] = { ...newItems[index], cost_type_vuid: '' };
                                      setContractItems(newItems);
                                      
                                      // Handle allocation update (cost type will be cleared)
                                      await handleCostCodeTypeChange(item.vuid, newCostCodeVuid, '');
                                    }
                                  } else {
                                    // Cost code cleared, clear cost type too
                                    newItems[index] = { ...newItems[index], cost_type_vuid: '' };
                                    setContractItems(newItems);
                                    
                                    // Handle allocation update (both will be cleared)
                                    await handleCostCodeTypeChange(item.vuid, '', '');
                                  }
                                }}
                                className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded text-sm"
                              >
                                <option value="">Select Cost Code</option>
                                {(() => {
                                  const allCostCodes = getAllCostCodes();
                                  const filteredCostCodes = allCostCodes.filter(isCostCodeSelectable);
                                  
                                  console.log(`🎯 Cost codes for item ${item.item_number}:`, {
                                    allCostCodes: allCostCodes.length,
                                    filteredCostCodes: filteredCostCodes.length,
                                    itemCostCodeVuid: item.cost_code_vuid,
                                    projectSettings: projectSettings,
                                    allowProjectCostCodes: projectSettings?.allow_project_cost_codes,
                                    availableOptions: filteredCostCodes.map(c => ({ vuid: c.vuid, code: c.code, description: c.description, is_project_specific: c.is_project_specific }))
                                  });
                                  
                                  return filteredCostCodes.map((costCode) => (
                                    <option key={costCode.vuid} value={costCode.vuid}>
                                      {costCode.code} - {costCode.description}
                                    </option>
                                  ));
                                })()}
                              </select>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <select
                                value={item.cost_type_vuid || ''}
                                onChange={async (e) => {
                                  const newCostTypeVuid = e.target.value;
                                  const oldMarkup = feeLevel === 'contract_item' 
                                    ? getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid) ?? (item.markup_percentage ?? 0)
                                    : (item.markup_percentage ?? 0);
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], cost_type_vuid: newCostTypeVuid };
                                  
                                  // Update markup and recalculate total_amount if fee level is contract_item
                                  if (feeLevel === 'contract_item' && item.cost_code_vuid && newCostTypeVuid) {
                                    const newMarkup = getMarkupFromFeeSettings(item.cost_code_vuid, newCostTypeVuid) ?? 0;
                                    const currentTotal = parseFloat(newItems[index].total_amount || 0);
                                    const recalculatedTotal = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
                                    newItems[index] = { 
                                      ...newItems[index], 
                                      markup_percentage: newMarkup,
                                      total_amount: recalculatedTotal
                                    };
                                  }
                                  
                                  setContractItems(newItems);
                                  
                                  // Handle allocation update
                                  await handleCostCodeTypeChange(item.vuid, item.cost_code_vuid, newCostTypeVuid);
                                }}
                                className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded text-sm"
                              >
                                <option value="">Select Cost Type</option>
                                {costTypes
                                  .filter(isCostTypeSelectable)
                                  .filter(costType => (
                                    projectBudgetLines.length === 0 ||
                                    projectBudgetLines.some(budgetLine => 
                                      budgetLine.cost_type_vuid === costType.vuid &&
                                      budgetLine.cost_code_vuid === item.cost_code_vuid
                                    )
                                  ))
                                  .map((costType) => (
                                   <option key={costType.vuid} value={costType.vuid}>
                                     {costType.cost_type}
                                   </option>
                                 ))}
                              </select>
                            </td>
                            {feeLevel === 'contract_item' && (
                              <td className="border border-gray-300 px-4 py-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  readOnly
                                  value={(() => {
                                    const markupFromSettings = getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid);
                                    return markupFromSettings !== null ? markupFromSettings : (item.markup_percentage ?? 0);
                                  })()}
                                  className="w-full px-2 py-1 border border-gray-300 bg-gray-50 cursor-not-allowed rounded"
                                  placeholder="0.0"
                                />
                              </td>
                            )}
                            <td className="border border-gray-300 px-4 py-2 align-top whitespace-normal break-words">
                              <textarea
                                value={item.description || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], description: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded resize-none"
                                placeholder="Description"
                                rows={2}
                              />
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                                <input
                                  type="text"
                                  value={item.total_amount || ''}
                                  onChange={(e) => {
                                    const newItems = [...contractItems];
                                    newItems[index] = { ...newItems[index], total_amount: e.target.value };
                                    setContractItems(newItems);
                                  }}
                                  className="w-full pl-6 pr-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-gray-50"
                                  placeholder="0.00"
                                />
                              </div>
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              <div className="text-sm font-medium">
                                {(() => {
                                  const changeOrderAmount = getChangeOrderAmount(item);
                                  return changeOrderAmount !== 0 ? (
                                    <span className={`${changeOrderAmount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                      {changeOrderAmount > 0 ? '+' : ''}{formatCurrency(changeOrderAmount)}
                                    </span>
                                  ) : (
                                    <span className="text-gray-400">$0.00</span>
                                  );
                                })()}
                              </div>
                              {(() => {
                                const changeOrderAmount = getChangeOrderAmount(item);
                                if (changeOrderAmount !== 0) {
                                  const matchingLines = changeOrderLines.filter(changeLine => 
                                    changeLine.cost_code_vuid === item.cost_code_vuid &&
                                    changeLine.cost_type_vuid === item.cost_type_vuid
                                  );
                                  return (
                                    <div className="text-xs text-gray-500 mt-1">
                                      {matchingLines.length} change order{matchingLines.length !== 1 ? 's' : ''}
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              <div className="text-sm font-semibold">
                                {(() => {
                                  const originalAmount = parseFloat(item.total_amount || 0) || 0;
                                  const changeOrderAmount = getChangeOrderAmount(item);
                                  const totalWithChanges = originalAmount + changeOrderAmount;
                                  
                                  return (
                                    <span className={`${totalWithChanges !== originalAmount ? 'text-blue-600' : 'text-gray-900'}`}>
                                      {formatCurrency(totalWithChanges)}
                                    </span>
                                  );
                                })()}
                              </div>
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <textarea
                                value={item.notes || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], notes: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded resize-none"
                                placeholder="Notes"
                                rows={2}
                              />
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              {console.log('🔧 Rendering Actions column for item:', item.item_number)}
                              <div className="flex space-x-2">
                                <button
                                  type="button"
                                  onClick={() => handleManageAllocations(item)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                  data-tooltip="Manage cost code and cost type allocations"
                                >
                                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                  Allocations
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(item.vuid)}
                                  className="inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-8 text-gray-500">
                          {budgetLinesProgress.total > 0 && budgetLinesProgress.current < budgetLinesProgress.total ? (
                            <div className="flex flex-col items-center space-y-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <p className="text-lg font-semibold text-blue-900">
                                Creating contract items from budget lines...
                              </p>
                              <p className="text-sm text-blue-700">
                                {budgetLinesProgress.current} of {budgetLinesProgress.total} items created
                              </p>
                            </div>
                          ) : (
                            'No contract items found. Add your first item below.'
                          )}
                        </td>
                      </tr>
                    )}
                    {/* New item row */}
                    <tr className="bg-blue-50">
                      <td className="border border-gray-300 px-4 py-2">
                        <input
                          type="text"
                          id="new-item-number"
                          value={newItemData.item_number}
                          onChange={(e) => setNewItemData({...newItemData, item_number: e.target.value})}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                          placeholder="Item #"
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        {projectSettings?.allow_project_cost_codes ? (
                          // Project-specific mode: text input
                          <input
                            type="text"
                            value={newItemData.cost_code_vuid || ''}
                            onChange={(e) => setNewItemData({...newItemData, cost_code_vuid: e.target.value})}
                            className="w-full px-2 py-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                            placeholder="Enter cost code..."
                          />
                        ) : (
                          // Global mode: dropdown
                          <select
                            value={newItemData.cost_code_vuid || ''}
                            onChange={(e) => setNewItemData({...newItemData, cost_code_vuid: e.target.value})}
                            className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50 text-sm"
                          >
                            <option value="">Select Cost Code</option>
                            {getAllCostCodes()
                              .filter(isCostCodeSelectable)
                              .filter(costCode => (
                                projectBudgetLines.length === 0 ||
                                projectBudgetLines.some(budgetLine => budgetLine.cost_code_vuid === costCode.vuid)
                              ))
                              .map((costCode) => (
                               <option key={costCode.vuid} value={costCode.vuid}>
                                 {costCode.code} - {costCode.description}
                               </option>
                             ))}
                          </select>
                        )}
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <select
                          value={newItemData.cost_type_vuid || ''}
                          onChange={(e) => setNewItemData({...newItemData, cost_type_vuid: e.target.value})}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50 text-sm"
                        >
                          <option value="">Select Cost Type</option>
                          {costTypes
                            .filter(isCostTypeSelectable)
                            .filter(costType => (
                              projectBudgetLines.length === 0 ||
                              projectBudgetLines.some(budgetLine => 
                                budgetLine.cost_type_vuid === costType.vuid &&
                                budgetLine.cost_code_vuid === newItemData.cost_code_vuid
                              )
                            ))
                            .map((costType) => (
                             <option key={costType.vuid} value={costType.vuid}>
                               {costType.cost_type}
                             </option>
                           ))}
                        </select>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <input
                          type="number"
                          step="0.1"
                          value={newItemData.markup_percentage ?? 0}
                          onChange={(e) => setNewItemData({...newItemData, markup_percentage: e.target.value})}
                          className="w-full px-2 py-1 border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                          placeholder="0.0"
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <textarea
                          value={newItemData.description}
                          onChange={(e) => setNewItemData({...newItemData, description: e.target.value})}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50 resize-none"
                          placeholder="Description"
                          rows={2}
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                          <input
                            type="text"
                            value={newItemData.total_amount}
                            onChange={(e) => setNewItemData({...newItemData, total_amount: e.target.value})}
                            className="w-full pl-6 pr-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <textarea
                          value={newItemData.notes}
                          onChange={(e) => setNewItemData({...newItemData, notes: e.target.value})}
                          className="w-full px-2 py-1 border-0 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded bg-blue-50 resize-none"
                          placeholder="Notes"
                          rows={2}
                        />
                      </td>
                      <td className="border border-gray-300 px-4 py-2">
                        <button
                          type="button"
                          onClick={handleAddNewItem}
                          className="bg-gray-50 hover:bg-gray-100 text-gray-900 font-semibold py-2 px-4 rounded-lg transition-colors"
                          data-tooltip="Add new contract item"
                        >
                          Add
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Save All Items and Done — neutral text (no maroon) */}
              <div className="mt-6 flex justify-center gap-4">
                <button
                  type="button"
                  onClick={handleSaveAllItems}
                  className="bg-gray-50 hover:bg-gray-100 text-gray-900 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Save All Items
                </button>
                <button
                  type="button"
                  onClick={handleDoneEditing}
                  className="bg-gray-50 hover:bg-gray-100 text-gray-900 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}

          {/* Change Order Lines Section */}
          {changeOrderLines && changeOrderLines.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Change Order Lines</h3>
              <div className="overflow-x-auto">
                <table className="min-w-max border border-gray-300">
                  <thead className="bg-blue-50">
                    <tr>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Change Order</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Cost Code</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Cost Type</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Description</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Contract Change</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">Budget Change</th>
                      <th className="border border-gray-300 px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeOrderLines.map((changeLine, index) => {
                      const costCode = getAllCostCodes().find(c => c.vuid === changeLine.cost_code_vuid);
                      const costType = costTypes.find(t => t.vuid === changeLine.cost_type_vuid);
                      const changeOrder = externalChangeOrders.find(co => co.vuid === changeLine.change_order_vuid);
                      
                      return (
                        <tr key={`change-${changeLine.vuid || index}`} className="hover:bg-blue-50">
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            <div className="font-medium">{changeLine.change_order_number || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{changeLine.change_order_date || ''}</div>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {costCode ? `${costCode.code} - ${costCode.description}` : 'N/A'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {costType ? `${costType.abbreviation} - ${costType.cost_type}` : 'N/A'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            {changeLine.notes || 'No description'}
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                            <span className={`font-medium ${(parseFloat(changeLine.contract_amount_change || 0) >= 0) ? 'text-green-600' : 'text-red-600'}`}>
                              {(parseFloat(changeLine.contract_amount_change || 0) >= 0) ? '+' : ''}{formatCurrency(changeLine.contract_amount_change || 0)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900 text-right">
                            <span className={`font-medium ${(parseFloat(changeLine.budget_amount_change || 0) >= 0) ? 'text-green-600' : 'text-red-600'}`}>
                              {(parseFloat(changeLine.budget_amount_change || 0) >= 0) ? '+' : ''}{formatCurrency(changeLine.budget_amount_change || 0)}
                            </span>
                          </td>
                          <td className="border border-gray-300 px-4 py-2 text-sm text-gray-900">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              changeLine.change_order_status === 'approved' ? 'bg-green-100 text-green-800' :
                              changeLine.change_order_status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {changeLine.change_order_status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Integration Modal */}
          {showIntegrationModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">Send Contract to Integration</h2>
                  <button 
                    onClick={() => { 
                      setShowIntegrationModal(false); 
                      setSelectedContractForIntegration(null); 
                      setSelectedIntegration(null); 
                    }} 
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* Contract Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Contract Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium text-gray-600">Contract Number:</span><p className="text-gray-900">{selectedContractForIntegration?.contract_number}</p></div>
                    <div><span className="font-medium text-gray-600">Contract Name:</span><p className="text-gray-900">{selectedContractForIntegration?.contract_name}</p></div>
                    <div><span className="font-medium text-gray-600">Type:</span><p className="text-gray-900 capitalize">{selectedContractForIntegration?.contract_type || 'Not specified'}</p></div>
                    <div><span className="font-medium text-gray-600">Amount:</span><p className="text-gray-900">{formatCurrency(selectedContractForIntegration?.contract_amount || 0)}</p></div>
                  </div>
                </div>
                {/* Integration Selection */}
                <div className="mb-6">
                  <label className="block text-lg font-semibold text-gray-900 mb-3">Select Integration *</label>
                  {integrations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <p className="text-lg">No integrations available</p>
                      <p className="text-sm">Create a Procore integration first to send contracts to external systems.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {integrations.map((integration) => (
                        <div 
                          key={integration.vuid} 
                          onClick={() => setSelectedIntegration(integration)} 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedIntegration?.vuid === integration.vuid 
                              ? 'border-gray-800 bg-gray-50' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                              <p className="text-sm text-gray-600 capitalize">{integration.integration_type.replace('_', ' ')}</p>
                              {integration.base_url && (
                                <p className="text-xs text-gray-500 mt-1">{integration.base_url}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                integration.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : integration.status === 'inactive' 
                                    ? 'bg-gray-100 text-gray-800' 
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {integration.status}
                              </span>
                              {selectedIntegration?.vuid === integration.vuid && (
                                <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end space-x-4">
                  <button 
                    onClick={() => { 
                      setShowIntegrationModal(false); 
                      setSelectedContractForIntegration(null); 
                      setSelectedIntegration(null); 
                    }} 
                    className="bg-gray-50 hover:bg-gray-100 text-gray-900 font-semibold py-2 px-6 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSendToIntegration} 
                    disabled={!selectedIntegration || sendingToIntegration} 
                    className={`font-semibold py-2 px-6 rounded-lg transition-colors ${
                      selectedIntegration && !sendingToIntegration
                        ? 'bg-gray-50 hover:bg-gray-100 text-gray-900' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {sendingToIntegration ? 'Sending...' : 'Send to Integration'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fee Settings Modal/View */}
      {showFeeSettings && selectedContract && (
        <ContractFeeSettings
          contractVuid={selectedContract.vuid}
          contractName={`${selectedContract.contract_number} - ${selectedContract.contract_name}`}
          onClose={async () => {
            setShowFeeSettings(false);
            // Refresh fee settings when modal closes to update markup values
            if (feeLevel === 'contract_item' && selectedContract) {
              try {
                const feeSettingsRes = await axios.get(`${baseURL}/api/contracts/${selectedContract.vuid}/fee-settings`);
                const newFeeSettings = feeSettingsRes.data || [];
                setFeeSettings(newFeeSettings);
                
                // Helper to get markup from new fee settings (before state update)
                const getMarkupFromNewSettings = (costCodeVuid, costTypeVuid) => {
                  const feeSetting = newFeeSettings.find(
                    setting => setting.cost_code_vuid === costCodeVuid && 
                               setting.cost_type_vuid === costTypeVuid
                  );
                  return feeSetting ? (feeSetting.fee_percentage || 0) : 0;
                };
                
                // Recalculate total_amount for all items based on updated markup from fee settings
                if (showItemsForm && contractItems.length > 0) {
                  const updatedItems = contractItems.map(item => {
                    if (!item.cost_code_vuid || !item.cost_type_vuid) {
                      return item;
                    }
                    
                    const oldMarkup = item.markup_percentage ?? 0;
                    const newMarkup = getMarkupFromNewSettings(item.cost_code_vuid, item.cost_type_vuid);
                    const currentTotal = parseFloat(item.total_amount || 0);
                    const recalculatedTotal = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
                    
                    return {
                      ...item,
                      markup_percentage: newMarkup,
                      total_amount: recalculatedTotal
                    };
                  });
                  
                  setContractItems(updatedItems);
                  
                  // Save updated items to database
                  const itemsToSave = updatedItems.filter(item => 
                    item.cost_code_vuid && item.cost_type_vuid && item.vuid && !item.vuid.startsWith('temp-')
                  );
                  
                  if (itemsToSave.length > 0) {
                    console.log(`🔄 Saving ${itemsToSave.length} items with updated markup to database...`);
                    Promise.all(itemsToSave.map(async (item) => {
                      try {
                        await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
                          total_amount: item.total_amount,
                          markup_percentage: item.markup_percentage
                        });
                      } catch (err) {
                        console.warn(`Failed to update item ${item.item_number}:`, err);
                      }
                    })).then(() => {
                      console.log('✅ All items updated with new markup values');
                    });
                  }
                }
              } catch (e) {
                console.warn('Failed to refresh fee settings:', e);
              }
            }
          }}
        />
      )}
    </div>
  );
};

export default ProjectContracts;
